import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, User, Check, Clock, ArrowRight, ArrowLeft, Trash2, ChevronDown, ChevronUp, Eye, Download, CheckCircle, MessageCircle } from 'lucide-react';
import { Button, Input, Select } from '../ui';
import SearchableSelect from '../ui/SearchableSelect';
import VendorDropdown from '../ui/VendorDropdown';
import FileViewerModal from '../common/FileViewerModal';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const formatDate = (dateString: string) => {
  if (!dateString || dateString.trim() === '') return 'No Date';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

interface BookingDetails {
  booking_status: string;
  itinerary_id: number;
  itinerary_status?: string;
  itinerary_name: string;
  lead_guest_name: string;
  agency_name?: string;
  travel_date?: string;
  no_of_pax?: number;
  no_of_children?: number;
  children_ages?: number[];
  accommodation_cost_items: AccommodationItem[];
  prebooked_cost_items: ActivityItem[];
  driver_paid_cost_items: ActivityItem[];
  transportation_cost_items: TransportationItem[];
  transportation_handled_by: string;
  operation_team_remarks?: string;
  total_estimated_cost?: number;
  total_actual_cost?: number;
  day_wise_details?: any[];
}

interface AccommodationItem {
  id: number;
  hotel_name: string;
  room_type: string;
  checkin_date: string;
  checkout_date: string;
  no_of_nights: number;
  no_of_rooms: number;
  no_of_extra_beds: number;
  cost: number;
  estimated_amount: number;
  booking_status: string;
  vendor: string;
  vendor_name: string;
  booking_reference: string;
  invoice_received: boolean;
  invoice_amount: number;
  remarks: string;
  unique_id?: string;
  due_date?: string;
}

interface ActivityItem {
  id: number;
  name: string;
  type: string;
  day: number;
  day_number: number;
  date: string;
  activity_date: string;
  cost: number;
  estimated_amount: number;
  booking_status: string;
  vendor: string;
  vendor_name: string;
  booking_reference: string;
  invoice_received: boolean;
  invoice_amount: number;
  remarks: string;
  unique_id?: string;
  activity_id?: number;
  meal_id?: number;
  due_date?: string;
  calculated_adults?: number;
  calculated_kids?: number;
  verification_status?: string;
}

interface TransportationItem {
  day: number;
  date: string;
  trip: string;
  trip_cost: number;
  parking?: number;
  overtime?: number;
  remarks?: string;
}

interface BookingModalProps {
  itineraryId: number;
  onClose: () => void;
  defaultTab?: 'accommodations' | 'prebooked' | 'driver_paid' | 'transportation' | 'report';
  reportOnly?: boolean;
}

const BookingModal: React.FC<BookingModalProps> = ({ itineraryId, onClose, defaultTab = 'accommodations', reportOnly = false }) => {
  const { isAdmin } = useAuth();
  const [details, setDetails] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'accommodations' | 'prebooked' | 'driver_paid' | 'transportation' | 'report'>(defaultTab);
  const [expandedItem, setExpandedItem] = useState<{ type: string; index?: number; uniqueId?: string } | null>(null);
  const [transportationHandledBy, setTransportationHandledBy] = useState('');
  const [transportationStatus, setTransportationStatus] = useState('NOT_BOOKED');
  const [hasChanges, setHasChanges] = useState(false);
  const [masterData, setMasterData] = useState<{ activities: { [key: string]: number | { activity_id: number; activity_key: string; activity_name: string; type: string; status: string } }; meals: { [key: string]: number } }>({ activities: {}, meals: {} });
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [reconciliation, setReconciliation] = useState<any>(null);
  const [quotationInfo, setQuotationInfo] = useState<{quotation_number: string; version_number: number} | null>(null);
  const [hotelPickerState, setHotelPickerState] = useState<{ item: ActivityItem; fromType: 'prebooked' | 'driver_paid' } | null>(null);
  const [dismissedReconciliations, setDismissedReconciliations] = useState<Set<string>>(new Set());

  const formatDateLong = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleQuickBook = (item: ActivityItem) => {
    if (!details) return;
    const grouped = details.prebooked_cost_items.filter(
      a => a.vendor === item.vendor && a.vendor && a.date === item.date
    );
    const lines = grouped.map(a => `* ${(a.calculated_adults || 0) + (a.calculated_kids || 0)} x ${a.name}`);
    const ref = quotationInfo ? `${quotationInfo.quotation_number}/${quotationInfo.version_number}` : `WH_${itineraryId}`;
    const kidsLine = (details.no_of_children || 0) > 0 ? `\nNo. of Kids : ${details.no_of_children}` : '';
    const agesLine = (details.no_of_children || 0) > 0 && details.children_ages?.length ? `\nKids Age : ${details.children_ages.join(', ')}` : '';
    const msg = `*BOOKING DETAILS*\n\nGuest Name : ${details.lead_guest_name || '—'}\nNo. of Adults : ${details.no_of_pax || 0}${kidsLine}${agesLine}\nActivities:\n${lines.join('\n')}\nDate : ${formatDateLong(item.date || item.activity_date)}\nRef: ${ref}`;
    navigator.clipboard.writeText(msg).then(() => alert('✅ Booking details copied to clipboard!'));
  };

  const loadDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const bookingResponse = await api.get(`/booking/details/${itineraryId}`);
      
      if (bookingResponse.data.success) {
        const data = bookingResponse.data.data;
        
        // 🔍 DEBUG LOGGING
        console.log('📦 BACKEND RESPONSE DATA:', {
          accommodation_count: data.accommodation_cost_items?.length || 0,
          prebooked_count: data.prebooked_cost_items?.length || 0,
          driver_paid_count: data.driver_paid_cost_items?.length || 0,
          transportation_count: data.transportation_cost_items?.length || 0,
          total_items: (data.accommodation_cost_items?.length || 0) + 
                      (data.prebooked_cost_items?.length || 0) + 
                      (data.driver_paid_cost_items?.length || 0) + 
                      (data.transportation_cost_items?.length || 0)
        });
        console.log('📦 Accommodation items:', data.accommodation_cost_items);
        console.log('📦 Prebooked items:', data.prebooked_cost_items);
        console.log('📦 Driver Paid items:', data.driver_paid_cost_items);
        console.log('📦 Transportation items:', data.transportation_cost_items);
        
        // day_wise_details should come from backend if needed for reports
        // Backend should include it in the booking response
        
        // Add unique IDs to items that don't have them (only if not already present)
        const addUniqueIds = (items: any[]) => {
          return items.map(item => {
            // Only add unique_id if it doesn't exist
            if (!item.unique_id) {
              return {
                ...item,
                unique_id: item.id?.toString() || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
              };
            }
            return item;
          });
        };
        
        data.accommodation_cost_items = addUniqueIds(data.accommodation_cost_items || []);
        data.prebooked_cost_items = addUniqueIds(data.prebooked_cost_items || []);
        data.driver_paid_cost_items = addUniqueIds(data.driver_paid_cost_items || []);
        
        setDetails(data);
        setTransportationHandledBy(data.transportation_handled_by || '');
        // Get transportation status from the first transportation item that has a status
        const transportationItems = data.transportation_cost_items || [];
        const firstTransportItem = transportationItems.find((item: any) => item.status) || transportationItems[0];
        setTransportationStatus(firstTransportItem?.status || 'NOT_BOOKED');
      }
    } catch (error: any) {
      console.error('❌ Error loading booking details:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to load booking details';
      setError(`Error loading data: ${errorMsg}`);
      alert(`❌ Error loading booking details: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const response = await api.get('/booking/master-data/all');
      if (response.data.success) {
        setMasterData({
          activities: response.data.data.activities || {},
          meals: response.data.data.meals || {}
        });
      }
    } catch (error: any) {
      console.error('Error loading master data:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to load master data';
      alert(`⚠️ Warning: Could not load master data (activities/meals). ${errorMsg}`);
    }
  };

  // Load data only once when component mounts
  useEffect(() => {    
    if (loadedRef.current) {
      console.log('⏭️ Skipping duplicate load for itinerary:', itineraryId);
      return;
    }
    
    console.log('✅ Loading data for itinerary:', itineraryId);
    loadedRef.current = true;
    
    loadDetails();
    loadMasterData();
    
    // Load reconciliation data (previous version bookings) + quotation info
    api.get(`/booking-reconciliation/${itineraryId}`).then(res => {
      if (res.data.success && res.data.needs_reconciliation) {
        setReconciliation(res.data);
      }
      // Set quotation info regardless of reconciliation
      if (res.data.quotation_number) {
        setQuotationInfo({
          quotation_number: res.data.quotation_number,
          version_number: res.data.current_version?.version_number
        });
      }
    }).catch(() => {
      // Fallback: fetch quotation info from version API
      api.get(`/itinerary-versions/${itineraryId}/quotation`).then(res => {
        if (res.data.success && res.data.quotation_number) {
          const currentVersion = res.data.versions?.find((v: any) => v.itinerary_id === itineraryId);
          setQuotationInfo({
            quotation_number: res.data.quotation_number,
            version_number: currentVersion?.version_number
          });
        }
      }).catch(() => {});
    });
    
    return () => {
      loadedRef.current = false; // Reset on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const addNewItem = (itemType: 'prebooked' | 'driver_paid', day: number) => {
    if (!details) return;
    
    // Calculate activity date from travel_date + day number
    let activityDate = '';
    if (details.travel_date) {
      const base = new Date(details.travel_date);
      base.setDate(base.getDate() + day - 1);
      activityDate = base.toISOString().split('T')[0];
    }
    
    const newItem: ActivityItem = {
      id: 0, // Temporary ID for new items
      unique_id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: 'New Item',
      type: 'addon',
      day: day,
      day_number: day,
      date: activityDate,
      activity_date: activityDate,
      cost: 0,
      estimated_amount: 0,
      booking_status: 'NOT_BOOKED',
      vendor: itemType === 'driver_paid' ? 'Driver' : '',
      vendor_name: itemType === 'driver_paid' ? 'Driver' : '',
      booking_reference: '',
      invoice_received: false,
      invoice_amount: 0,
      remarks: '',
      due_date: ''
    };
    
    const newDetails = { ...details };
    if (itemType === 'prebooked') {
      newDetails.prebooked_cost_items = [...newDetails.prebooked_cost_items, newItem];
    } else {
      newDetails.driver_paid_cost_items = [...newDetails.driver_paid_cost_items, newItem];
    }
    
    setDetails(newDetails);
    setHasChanges(true);
  };

  const updateItemByUniqueId = (itemType: string, uniqueId: string, updateData: any) => {
    if (!details || !uniqueId) return;
    
    // Show warning if changing to PENDING_PAYMENT
    if (updateData.booking_status === 'PENDING_PAYMENT') {
      const confirmed = window.confirm(
        '⚠️ Invoice Upload Required\n\nThis item will require invoice upload. Make sure the vendor uploads their invoice.\n\nContinue?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    const newDetails = { ...details };
    
    if (itemType === "accommodation") {
      const items_array = [...newDetails.accommodation_cost_items];
      const itemIndex = items_array.findIndex(item => item.unique_id === uniqueId || item.id?.toString() === uniqueId);
      if (itemIndex >= 0) {
        items_array[itemIndex] = { ...items_array[itemIndex], ...updateData };
        newDetails.accommodation_cost_items = items_array;
      }
    } else if (itemType === "prebooked") {
      const items_array = [...newDetails.prebooked_cost_items];
      const itemIndex = items_array.findIndex(item => item.unique_id === uniqueId || item.id?.toString() === uniqueId);
      if (itemIndex >= 0) {
        items_array[itemIndex] = { ...items_array[itemIndex], ...updateData };
        newDetails.prebooked_cost_items = items_array;
      }
    } else if (itemType === "driver_paid") {
      const items_array = [...newDetails.driver_paid_cost_items];
      const itemIndex = items_array.findIndex(item => item.unique_id === uniqueId || item.id?.toString() === uniqueId);
      if (itemIndex >= 0) {
        items_array[itemIndex] = { ...items_array[itemIndex], ...updateData };
        newDetails.driver_paid_cost_items = items_array;
      }
    }
    
    setDetails(newDetails);
    setHasChanges(true);
  };

  const moveItemBetweenSections = (item: ActivityItem, fromType: 'prebooked' | 'driver_paid') => {
    if (!details) return;
    const toType = fromType === 'prebooked' ? 'Driver Paid' : 'Prebooked';
    if (!window.confirm(`Move "${item.name}" to ${toType}?`)) return;
    
    const newDetails = { ...details };
    
    if (fromType === 'prebooked') {
      newDetails.prebooked_cost_items = newDetails.prebooked_cost_items.filter(i => i.unique_id !== item.unique_id);
      newDetails.driver_paid_cost_items = [...newDetails.driver_paid_cost_items, item];
    } else {
      newDetails.driver_paid_cost_items = newDetails.driver_paid_cost_items.filter(i => i.unique_id !== item.unique_id);
      newDetails.prebooked_cost_items = [...newDetails.prebooked_cost_items, item];
    }
    
    setDetails(newDetails);
    setHasChanges(true);
  };

  const moveItemToAccommodation = (item: ActivityItem, fromType: 'prebooked' | 'driver_paid') => {
    if (!details) return;
    // If multiple hotels, show picker; if one, confirm directly
    const hotels = details.accommodation_cost_items;
    if (hotels.length === 0) {
      alert('No accommodations found to move this item to.');
      return;
    }
    if (hotels.length === 1) {
      if (!window.confirm(`Move "${item.name}" to Accommodation (${hotels[0].hotel_name})?`)) return;
      executeAccommodationMove(item, fromType, hotels[0].hotel_name);
    } else {
      setHotelPickerState({ item, fromType });
    }
  };

  const executeAccommodationMove = (item: ActivityItem, fromType: 'prebooked' | 'driver_paid', hotelName: string) => {
    if (!details) return;
    const newDetails = { ...details };

    if (fromType === 'prebooked') {
      newDetails.prebooked_cost_items = newDetails.prebooked_cost_items.filter(i => i.unique_id !== item.unique_id);
    } else {
      newDetails.driver_paid_cost_items = newDetails.driver_paid_cost_items.filter(i => i.unique_id !== item.unique_id);
    }

    // Get vendor from the matching hotel
    const matchedHotel = newDetails.accommodation_cost_items.find(a => a.hotel_name === hotelName);
    const hotelVendor = matchedHotel?.vendor || matchedHotel?.vendor_name || '';

    const itemDate = item.date || item.activity_date;
    const accItem: AccommodationItem = {
      id: item.id,
      unique_id: item.unique_id,
      hotel_name: hotelName,
      room_type: item.name,
      checkin_date: itemDate || '',
      checkout_date: itemDate || '',
      no_of_nights: 0,
      no_of_rooms: 0,
      no_of_extra_beds: 0,
      cost: item.cost || item.estimated_amount || 0,
      estimated_amount: item.estimated_amount || item.cost || 0,
      booking_status: item.booking_status || 'NOT_BOOKED',
      vendor: hotelVendor,
      vendor_name: hotelVendor,
      booking_reference: item.booking_reference || '',
      invoice_received: item.invoice_received || false,
      invoice_amount: item.invoice_amount || 0,
      remarks: item.remarks || '',
      due_date: item.due_date,
      source_type: item.type === 'addon' ? 'Day Extra' : item.type === 'meal' ? 'Meal' : 'Day Addon',
    } as AccommodationItem;

    newDetails.accommodation_cost_items = [...newDetails.accommodation_cost_items, accItem];
    setDetails(newDetails);
    setHasChanges(true);
  };

  const moveItemFromAccommodation = (item: AccommodationItem) => {
    if (!details) return;
    if (!window.confirm(`Move "${item.room_type || item.hotel_name}" back to Prebooked?`)) return;

    const newDetails = { ...details };
    const uid = (item as any).unique_id || item.id?.toString();
    newDetails.accommodation_cost_items = newDetails.accommodation_cost_items.filter(i => {
      const iUid = (i as any).unique_id || i.id?.toString();
      return iUid !== uid;
    });

    const actItem: ActivityItem = {
      id: item.id,
      unique_id: uid,
      name: item.room_type || item.hotel_name,
      type: 'addon',
      day: 0,
      day_number: 0,
      date: item.checkin_date,
      activity_date: item.checkin_date,
      cost: item.cost || 0,
      estimated_amount: item.estimated_amount || item.cost || 0,
      booking_status: item.booking_status || 'NOT_BOOKED',
      vendor: item.vendor || '',
      vendor_name: item.vendor_name || item.vendor || '',
      booking_reference: item.booking_reference || '',
      invoice_received: item.invoice_received || false,
      invoice_amount: item.invoice_amount || 0,
      remarks: item.remarks || '',
      due_date: item.due_date,
    } as ActivityItem;

    newDetails.prebooked_cost_items = [...newDetails.prebooked_cost_items, actItem];
    setDetails(newDetails);
    setHasChanges(true);
  };

  const deleteItem = async (itemType: 'prebooked' | 'driver_paid', uniqueId: string) => {
    if (!details || !uniqueId) return;
    
    const confirmed = window.confirm('Are you sure you want to delete this item?');
    if (!confirmed) return;
    
    // Find the item to get its ID
    const items = itemType === 'prebooked' ? details.prebooked_cost_items : details.driver_paid_cost_items;
    const item = items.find(item => item.unique_id === uniqueId);
    
    if (item && item.id && item.id > 0) {
      // Delete from database immediately
      try {
        const response = await api.delete(`/booking/delete-item-v2/${item.id}`);
        if (response.data.success) {
          // Success - item deleted from database
        }
      } catch (error: any) {
        console.error('❌ Error deleting item from database:', error);
        const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
        alert(`❌ Error deleting item: ${errorMsg}. Please try again.`);
        return;
      }
    }
    
    // Remove from local state
    const newDetails = { ...details };
    
    if (itemType === 'prebooked') {
      newDetails.prebooked_cost_items = newDetails.prebooked_cost_items.filter(item => item.unique_id !== uniqueId);
    } else {
      newDetails.driver_paid_cost_items = newDetails.driver_paid_cost_items.filter(item => item.unique_id !== uniqueId);
    }
    
    setDetails(newDetails);
    setHasChanges(true);
  };

  const saveAllChanges = async () => {
    if (!details || !hasChanges) return;
    
    try {
      setSaving(true);
      
      // Get all current item IDs from the UI state
      const currentItemIds = new Set([
        ...details.accommodation_cost_items.filter(item => item.id && item.id > 0).map(item => item.id),
        ...details.prebooked_cost_items.filter(item => item.id && item.id > 0).map(item => item.id),
        ...details.driver_paid_cost_items.filter(item => item.id && item.id > 0).map(item => item.id)
      ]);
      
      // Validate pending payment items have due dates
      const allItems = [
        ...details.accommodation_cost_items.map(item => ({ ...item, section_type: 'accommodation' })),
        ...details.prebooked_cost_items.map(item => ({ ...item, section_type: 'prebooked' })),
        ...details.driver_paid_cost_items.map(item => ({ ...item, section_type: 'driver_paid' }))
      ];
      
      // Validate pending payment items have due dates
      const invalidPaymentItems = allItems.filter(item => 
        item.booking_status === 'PENDING_PAYMENT' && !item.due_date
      );
      
      if (invalidPaymentItems.length > 0) {
        alert(`Payment due date is required for items with Pending Payment status. Please set due dates for ${invalidPaymentItems.length} item(s).`);
        return;
      }
      
      // Validate prebooked items have vendors when status != NOT_BOOKED
      const prebookedItemsWithoutVendors = allItems.filter(item => 
        (item as any).section_type === 'prebooked' && 
        item.booking_status !== 'NOT_BOOKED' && 
        (!item.vendor && !item.vendor_name)
      );
      
      if (prebookedItemsWithoutVendors.length > 0) {
        const itemNames = prebookedItemsWithoutVendors.map(item => `"${(item as any).name}" (Day ${(item as any).day || (item as any).day_number || 0})`).join(', ');
        alert(`Vendor is required for prebooked items with booking status. Please assign vendors to: ${itemNames}`);
        return;
      }
      
      // Validate activity/meal/addon items
      const activityItems = allItems.filter(item => (item as any).section_type !== 'accommodation');
      const validationErrors: string[] = [];
      
      for (const item of activityItems) {
        const activityItem = item as any;
        const itemType = activityItem.type || 'activity';
        const itemName = activityItem.name || 'Unnamed item';
        
        // Validate activities
        if (itemType === 'activity') {
          if (!activityItem.activity_id || activityItem.activity_id === 0) {
            validationErrors.push(`${itemName}: Please select a valid activity`);
          }
          if (activityItem.cost === null || activityItem.cost === undefined || activityItem.cost < 0) {
            validationErrors.push(`${itemName}: Please enter a valid cost (0 or greater)`);
          }
          if (activityItem.section_type === 'prebooked' && (!activityItem.booking_status || activityItem.booking_status === '')) {
            validationErrors.push(`${itemName}: Please select a booking status`);
          }
        }
        
        // Validate meals
        if (itemType === 'meal') {
          if (!activityItem.meal_id || activityItem.meal_id === 0) {
            validationErrors.push(`${itemName}: Please select a valid meal`);
          }
          if (activityItem.cost === null || activityItem.cost === undefined || activityItem.cost < 0) {
            validationErrors.push(`${itemName}: Please enter a valid cost (0 or greater)`);
          }
          if (activityItem.section_type === 'prebooked' && (!activityItem.booking_status || activityItem.booking_status === '')) {
            validationErrors.push(`${itemName}: Please select a booking status`);
          }
        }
        
        // Validate addons
        if (itemType === 'addon') {
          if (!activityItem.name || activityItem.name.trim() === '' || activityItem.name === 'New Item') {
            validationErrors.push(`Addon item: Please enter a valid name`);
          }
          if (activityItem.cost === null || activityItem.cost === undefined || activityItem.cost < 0) {
            validationErrors.push(`${itemName}: Please enter a valid cost (0 or greater)`);
          }
          if (activityItem.section_type === 'prebooked' && (!activityItem.booking_status || activityItem.booking_status === '')) {
            validationErrors.push(`${itemName}: Please select a booking status`);
          }
        }
      }
      
      if (validationErrors.length > 0) {
        alert(`Please fix the following validation errors:\n\n${validationErrors.join('\n')}`);
        return;
      }
      
      // Also update all driver_paid items with the transportation status and driver name
      if (transportationHandledBy !== details.transportation_handled_by || transportationStatus !== (details as any).transportation_status) {
        try {
          const driverUpdateData = {
            driver_name: transportationHandledBy,
            status: transportationStatus
          };
          
          const response = await api.put(`/booking/update-driver-name/${itineraryId}`, driverUpdateData);
        } catch (error) {
          console.error('❌ Error updating transportation items:', error);
        }
      }
      
      // Save each item using V2 endpoint - BULK UPDATE
      const itemsToUpdate = [];
      const itemsToCreate = [];
      
      for (const item of allItems) {
        const saveData = {
          booking_status: item.booking_status,
          vendor_name: item.vendor || item.vendor_name,
          booking_reference: item.booking_reference,
          remarks: item.remarks,
          due_date: item.due_date,
          cost: item.cost || item.estimated_amount,
          item_type: (item as any).section_type === 'prebooked' ? 'PREBOOKED' : 
                    (item as any).section_type === 'driver_paid' ? 'DRIVER_PAID' : 
                    (item as any).section_type === 'accommodation' ? 'ACCOMMODATION' : 'PREBOOKED'
        };
        
        if (item.id && item.id > 0) {
          // Prepare for bulk UPDATE
          console.log(`🔄 UPDATE: "${(item as any).name}" (ID: ${item.id}) - Has existing ID, will update`);
          
          const updatePayload: any = {
            id: item.id,
            ...saveData,
            meal_id: (item as any).meal_id,
            activity_id: (item as any).activity_id,
            item_subtype: (item as any).type,
            name: (item as any).name || (item as any).room_type || (item as any).hotel_name,
            payment_date: (item as any).payment_date
          };
          
          // Include accommodation-specific fields for accommodation items
          if ((item as any).section_type === 'accommodation') {
            updatePayload.hotel_name = (item as any).hotel_name || null;
            updatePayload.room_type = (item as any).room_type || null;
            updatePayload.no_of_nights = (item as any).no_of_nights ?? 0;
            updatePayload.no_of_rooms = (item as any).no_of_rooms ?? 0;
            updatePayload.checkin_date = (item as any).checkin_date || null;
            updatePayload.checkout_date = (item as any).checkout_date || null;
          }
          
          itemsToUpdate.push(updatePayload);
        } else {
          // Prepare for CREATE
          console.log(`➕ CREATE: "${(item as any).name}" (ID: ${item.id || 'null'}) - No ID, will create new`);
          
          itemsToCreate.push({
            itinerary_id: itineraryId,
            item_type: (item as any).section_type === 'prebooked' ? 'PREBOOKED' : 'DRIVER_PAID',
            item_subtype: ((item as any).type || 'activity').toLowerCase(),
            name: (item as any).name || 'New Item',
            day_number: (item as any).day || (item as any).day_number || 0,
            estimated_amount: item.cost || 0,
            booking_status: item.booking_status,
            vendor_name: item.vendor || item.vendor_name,
            booking_reference: item.booking_reference || null,
            remarks: item.remarks || null,
            due_date: item.due_date || null,
            activity_id: (item as any).activity_id,
            meal_id: (item as any).meal_id
          });
        }
      }
      
      // Bulk update all items in one API call
      if (itemsToUpdate.length > 0) {
        console.log(`📦 BULK UPDATE: Updating ${itemsToUpdate.length} items in one call`);
        await api.put(`/booking/bulk-update-items-v2`, itemsToUpdate);
      }
      
      // Create new items (still sequential for now)
      for (const createData of itemsToCreate) {
        await api.post(`/booking/create-item-v2`, createData);
      }
      
      // Save transportation items using bulk update
      if (details.transportation_cost_items && details.transportation_cost_items.length > 0) {
        const transportItemsToUpdate = [];
        
        for (const transportItem of details.transportation_cost_items) {
          if ((transportItem as any).id && (transportItem as any).hasChanges) {
            transportItemsToUpdate.push({
              id: (transportItem as any).id,
              trip_cost: transportItem.trip_cost || 0,
              parking: (transportItem as any).parking || 0,
              overtime: (transportItem as any).overtime || 0,
              remarks: (transportItem as any).remarks || ''
            });
          }
        }
        
        if (transportItemsToUpdate.length > 0) {
          console.log(`📦 BULK UPDATE: Updating ${transportItemsToUpdate.length} transportation items in one call`);
          await api.put(`/booking/bulk-update-items-v2`, transportItemsToUpdate);
        }
      }
      
      setHasChanges(false);
      // Only reload booking details, not itinerary calculation
      const bookingResponse = await api.get(`/booking/details/${itineraryId}`);
      if (bookingResponse.data.success) {
        const data = bookingResponse.data.data;
        setDetails(data);
      }
      // Ensure hasChanges stays false after reload
      setHasChanges(false);
    } catch (error: any) {
      console.error('❌ Error saving changes:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error saving changes: ${errorMsg}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const updateBookingStatus = async (status: string) => {
    try {
      await api.put(`/booking/update-status/${itineraryId}`, {
        booking_status: status
      });
      loadDetails();
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error updating booking status: ${errorMsg}`);
    }
  };

  const markOperationComplete = async () => {
    if (!details) return;
    
    try {
      // Validation checks
      const validationErrors: string[] = [];
      
      // Check if driver is assigned
      if (!transportationHandledBy || transportationHandledBy.trim() === '') {
        validationErrors.push('Driver name must be assigned');
      }

      // Check transportation is booked
      if (transportationStatus === 'NOT_BOOKED') {
        validationErrors.push('Transportation must be booked');
      }
      
      // Check no items are in BOOKED or NOT_BOOKED status
      const invalidStatuses = ['BOOKED', 'NOT_BOOKED'];

      const prebookedInvalid = details.prebooked_cost_items.filter(item => 
        invalidStatuses.includes(item.booking_status || 'NOT_BOOKED')
      );
      if (prebookedInvalid.length > 0) {
        validationErrors.push(`${prebookedInvalid.length} prebooked item(s) still in ${prebookedInvalid.map(i => i.booking_status || 'NOT_BOOKED').filter((v, i, a) => a.indexOf(v) === i).join('/')} status`);
      }
      
      const accommodationInvalid = details.accommodation_cost_items.filter(item => 
        invalidStatuses.includes(item.booking_status || 'NOT_BOOKED')
      );
      if (accommodationInvalid.length > 0) {
        validationErrors.push(`${accommodationInvalid.length} accommodation item(s) still in ${accommodationInvalid.map(i => i.booking_status || 'NOT_BOOKED').filter((v, i, a) => a.indexOf(v) === i).join('/')} status`);
      }
      
      // Check file uploads (minimum 2 files per vendor - one receipt and one operational)
      const allVendorIds = Array.from(new Set([
        ...details.accommodation_cost_items.map(item => (item as any).vendor_id).filter(Boolean),
        ...details.prebooked_cost_items.map(item => (item as any).vendor_id).filter(Boolean)
      ]));
      
      for (const vendorId of allVendorIds) {
        try {
          const filesRes = await api.get(`/operational-cost/${itineraryId}/files/all?vendor_id=${vendorId}`);
          if (filesRes.data.success) {
            const vendorFiles = filesRes.data.data || [];
            const receiptFiles = vendorFiles.filter((f: any) => f.file_type === 'receipt');
            const operationalFiles = vendorFiles.filter((f: any) => f.file_type === 'operational');
            
            // Get vendor name for error message
            const vendorName = vendorFiles[0]?.vendor_name || `Vendor ID ${vendorId}`;
            
            if (receiptFiles.length === 0) {
              validationErrors.push(`${vendorName}: Missing receipt file`);
            }
            if (operationalFiles.length === 0) {
              validationErrors.push(`${vendorName}: Missing operational file`);
            }
          }
        } catch (error) {
          validationErrors.push(`Vendor ID ${vendorId}: Could not verify file uploads`);
        }
      }
      
      if (validationErrors.length > 0) {
        alert(`Cannot mark as complete. Please fix the following issues:\n\n${validationErrors.join('\n')}`);
        return;
      }
      
      // Confirm action
      const confirmed = window.confirm(
        '✅ Mark Operation Complete\n\nThis will mark the operational cost status as COMPLETED. This action cannot be undone.\n\nContinue?'
      );
      
      if (!confirmed) return;
      
      // Update operational cost status
      const response = await api.put(`/booking/mark-complete/${itineraryId}`);
      
      if (response.data.success) {
        alert('✅ Operation marked as complete successfully!');
        onClose(); // Close modal and refresh
      } else {
        alert('❌ Failed to mark operation as complete. Please try again.');
      }
    } catch (error: any) {
      console.error('Error marking operation complete:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error marking operation as complete: ${errorMsg}. Please try again.`);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md">
          <p className="text-red-600 font-semibold mb-2">❌ Error loading booking details</p>
          {error && <p className="text-sm text-gray-700 mb-4">{error}</p>}
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[98vw] sm:max-w-[95vw] max-h-[98vh] sm:max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex justify-between items-start sm:items-center">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-base sm:text-lg font-bold truncate">💰 Booking Management</h2>
              <p className="text-xs sm:text-sm text-white text-opacity-90 truncate">
                WH_{details.itinerary_id} • {details.lead_guest_name || details.itinerary_name || 'Unknown Guest'}
                {quotationInfo && (
                  <span className="ml-2 px-1.5 py-0.5 bg-white bg-opacity-30 rounded text-[10px] font-bold font-mono">
                    {quotationInfo.quotation_number}/{quotationInfo.version_number}
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0 mr-2">
              <div className="text-xs text-white text-opacity-90">
                💰 Est: IDR {(details.total_estimated_cost || 0).toLocaleString()}
              </div>
              <div className="text-xs text-white text-opacity-90">
                ✅ Actual: IDR {(() => {
                  const accommodationTotal = details.accommodation_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
                  const prebookedTotal = details.prebooked_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
                  const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
                  const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
                    const tripCost = item.trip_cost || 0;
                    const parking = (item as any).parking || 0;
                    const overtime = (item as any).overtime || 0;
                    return sum + tripCost + parking + overtime;
                  }, 0);
                  return accommodationTotal + prebookedTotal + driverPaidTotal + transportationTotal;
                })().toLocaleString()}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Guest Details Section */}
        <div className="px-3 sm:px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-gray-600 font-medium">Agency:</span>
              <p className="text-gray-900 truncate">{details.agency_name || 'Unknown Agency'}</p>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Lead Guest:</span>
              <p className="text-gray-900 truncate">{details.lead_guest_name || 'Unknown'}</p>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Travel Date:</span>
              <p className="text-gray-900">{details.travel_date ? formatDate(details.travel_date) : 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Pax:</span>
              <p className="text-gray-900">
                {details.no_of_pax || 0} + {details.no_of_children || 0} kids
                {details.children_ages && details.children_ages.length > 0 && (
                  <span className="text-gray-600 text-xs ml-1">
                    (ages: {details.children_ages.join(', ')})
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Operation Team Remarks Section */}
        {details.operation_team_remarks && (
          <div className="px-3 sm:px-4 py-3 bg-orange-50 border-b border-orange-200">
            <div className="flex items-start gap-2">
              <span className="text-orange-600 font-medium text-sm flex-shrink-0">📝 Operation Team Remarks:</span>
              <p className="text-orange-800 text-sm font-medium">{details.operation_team_remarks}</p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="px-3 sm:px-4 py-3 bg-gray-50 border-b border-gray-200">
        </div>

        {/* Tabs */}
        {!reportOnly && (
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('accommodations')}
                className={`flex-1 px-2 py-3 font-medium text-sm text-center ${
                  activeTab === 'accommodations'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🏨 Accommodations ({details.accommodation_cost_items.length})
              </button>
              <button
                onClick={() => setActiveTab('prebooked')}
                className={`flex-1 px-2 py-3 font-medium text-sm text-center ${
                  activeTab === 'prebooked'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🎫 Prebooked ({details.prebooked_cost_items.length})
              </button>
              <button
                onClick={() => setActiveTab('driver_paid')}
                className={`flex-1 px-2 py-3 font-medium text-sm text-center ${
                  activeTab === 'driver_paid'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                💰 Driver Paid ({details.driver_paid_cost_items.length})
              </button>
              <button
                onClick={() => setActiveTab('transportation')}
                className={`flex-1 px-2 py-3 font-medium text-sm text-center ${
                  activeTab === 'transportation'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🚗 Transportation ({details.transportation_cost_items?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('report')}
                className={`flex-1 px-2 py-3 font-medium text-sm text-center ${
                  activeTab === 'report'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📊 Report
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {(reportOnly || activeTab === 'report') && (
            <ReportTab details={details} itineraryId={itineraryId} onMarkComplete={markOperationComplete} />
          )}
          
          {!reportOnly && activeTab === 'accommodations' && (
            <div className="p-3 sm:p-4 space-y-3 pb-16 sm:pb-12">
              {/* Cancellation Alert */}
              {details.itinerary_status === 'CANCELED' && (
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 text-sm">
                  <div className="font-bold text-red-700 mb-1">🚨 ITINERARY CANCELED — Vendor Bookings Must Be Cancelled</div>
                  <div className="text-red-600 text-xs">Please cancel all active bookings with vendors for this itinerary.</div>
                </div>
              )}
              {/* Reconciliation Banner */}
              {reconciliation?.needs_reconciliation && !reconciliation.cancellation_mode && reconciliation.summary?.prev_only > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="font-medium text-amber-800 mb-1">⚠️ Previous version ({reconciliation.previous_version?.wyn_id}) had bookings</div>
                  <div className="text-amber-700 text-xs">
                    {reconciliation.summary.matched} matched · {reconciliation.summary.prev_only} removed · {reconciliation.summary.current_only} new
                  </div>
                </div>
              )}
              {(() => {
                // Separate real hotels from moved addons
                const realHotels = details.accommodation_cost_items.filter(a => a.no_of_nights > 0);
                const movedAddons = details.accommodation_cost_items.filter(a => !a.no_of_nights || a.no_of_nights === 0);

                return realHotels.map((acc, index) => {
                  // Find addons tagged as "Accommodation 🏨" from day_wise_details
                  const accommodationAddons = (() => {
                    if (!details.day_wise_details) return [];
                    const accCheckin = new Date(acc.checkin_date);
                    const accCheckout = new Date(acc.checkout_date);
                    const addonNames = new Set<string>();
                    
                    for (const dayDetail of details.day_wise_details) {
                      const dayDate = dayDetail.tour_date ? new Date(dayDetail.tour_date) : null;
                      if (!dayDate || dayDate < accCheckin || dayDate >= accCheckout) continue;
                      for (const addon of (dayDetail.day_addons || [])) {
                        if (addon.main_activity_name === 'Accommodation 🏨') {
                          addonNames.add(addon.activity_name);
                        }
                      }
                    }
                    if (addonNames.size === 0) return [];
                    return [...details.prebooked_cost_items, ...details.driver_paid_cost_items].filter(item => {
                      const itemDate = item.date || item.activity_date;
                      if (!itemDate) return false;
                      const d = new Date(itemDate);
                      return d >= accCheckin && d < accCheckout && addonNames.has(item.name);
                    });
                  })();

                  // Moved addons grouped under this hotel
                  const hotelAddons = movedAddons.filter(a => 
                    a.hotel_name === acc.hotel_name || 
                    (!a.hotel_name && a.vendor && a.vendor === acc.vendor)
                  );

                  // Find matching previous booking
                  const prevMatch = reconciliation?.matched?.find((m: any) =>
                    m.current?.hotel_name && acc.hotel_name &&
                    m.current.hotel_name.toLowerCase() === acc.hotel_name.toLowerCase()
                  );
                  const prevItem = prevMatch?.prev;
                  return (
                    <div key={index}>
                      {prevItem && prevItem.status !== 'NOT_BOOKED' && !dismissedReconciliations.has(`acc_${prevItem.id}`) && (
                        <div className="mb-2 border border-indigo-200 rounded-lg overflow-hidden">
                          <div className="p-2.5 bg-indigo-50 flex items-center justify-between">
                            <span className="text-xs font-medium text-indigo-700">
                              📌 Previously booked in {reconciliation.previous_version?.wyn_id}
                            </span>
                            <div className="flex gap-2">
                              {acc.booking_status === 'NOT_BOOKED' && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await api.post(`/booking-reconciliation/${itineraryId}/carry-forward`, {
                                        prev_item_id: prevItem.id,
                                        current_item_id: acc.id
                                      });
                                      loadedRef.current = false;
                                      loadDetails();
                                      api.get(`/booking-reconciliation/${itineraryId}`).then(res => {
                                        if (res.data.success && res.data.needs_reconciliation) setReconciliation(res.data);
                                        else setReconciliation(null);
                                      });
                                    } catch (err) { console.error(err); }
                                  }}
                                  className="px-2 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                  Copy from previous
                                </button>
                              )}
                              <button
                                onClick={() => setDismissedReconciliations(prev => new Set(prev).add(`acc_${prevItem.id}`))}
                                className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700"
                              >
                                ✅ Done
                              </button>
                            </div>
                          </div>
                          <div className="p-2.5 bg-white">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                                <div className="font-semibold text-red-800 mb-1.5">Previous ({reconciliation.previous_version?.wyn_id})</div>
                                <div className="space-y-0.5 text-gray-700">
                                  <div>Check-in: <strong>{prevItem.checkin_date ? new Date(prevItem.checkin_date).toLocaleDateString('en-GB') : '—'}</strong></div>
                                  <div>Check-out: <strong>{prevItem.checkout_date ? new Date(prevItem.checkout_date).toLocaleDateString('en-GB') : '—'}</strong></div>
                                  <div>Nights: <strong>{prevItem.no_of_nights || '—'}</strong> · Rooms: <strong>{prevItem.no_of_rooms || '—'}</strong></div>
                                  <div>Kids BF: <strong>{prevItem.calculated_kids || 0}</strong></div>
                                  <div>Cost: <strong>{prevItem.estimated_amount?.toLocaleString()}</strong></div>
                                  <div className="pt-1 border-t border-red-200 mt-1">
                                    <div>Vendor: <strong className="text-blue-700">{prevItem.vendor_name || '—'}</strong></div>
                                    <div>Status: <strong>{prevItem.status}</strong></div>
                                    {prevItem.booking_reference && <div>Ref: <strong>{prevItem.booking_reference}</strong></div>}
                                    {prevItem.invoice_number && <div>Invoice: <strong>{prevItem.invoice_number}</strong></div>}
                                    {prevItem.payment_date && <div>Paid: <strong>{new Date(prevItem.payment_date).toLocaleDateString('en-GB')}</strong></div>}
                                  </div>
                                  {reconciliation.prev_vendor_files?.filter((f: any) => f.vendor_name === prevItem.vendor_name).length > 0 && (
                                    <div className="pt-1 border-t border-red-200 mt-1">
                                      <div className="font-medium text-gray-600">📎 Files:</div>
                                      {reconciliation.prev_vendor_files.filter((f: any) => f.vendor_name === prevItem.vendor_name).map((f: any) => (
                                        <div key={f.id} className="text-blue-600 truncate">{f.file_name} ({f.file_type})</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                <div className="font-semibold text-blue-800 mb-1.5">Current (WH_{itineraryId})</div>
                                <div className="space-y-0.5 text-gray-700">
                                  <div>Check-in: <strong>{acc.checkin_date ? new Date(acc.checkin_date).toLocaleDateString('en-GB') : '—'}</strong></div>
                                  <div>Check-out: <strong>{acc.checkout_date ? new Date(acc.checkout_date).toLocaleDateString('en-GB') : '—'}</strong></div>
                                  <div>Nights: <strong>{acc.no_of_nights || '—'}</strong> · Rooms: <strong>{acc.no_of_rooms || '—'}</strong></div>
                                  <div>Extra beds: <strong>{acc.no_of_extra_beds || 0}</strong></div>
                                  <div>Cost: <strong>{acc.cost?.toLocaleString() || acc.estimated_amount?.toLocaleString()}</strong></div>
                                  <div className="pt-1 border-t border-blue-200 mt-1">
                                    <div>Vendor: <strong>{acc.vendor || '—'}</strong></div>
                                    <div>Status: <strong>{acc.booking_status || 'NOT_BOOKED'}</strong></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {prevMatch?.has_differences && prevMatch.differences?.length > 0 && (
                              <div className="mt-2 p-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                                Δ Changes: {prevMatch.differences.join(' · ')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <AccommodationCard
                        item={acc}
                        index={index}
                        expanded={expandedItem?.type === 'accommodation' && expandedItem?.index === index}
                        onToggleExpand={() => {
                          if (expandedItem?.type === 'accommodation' && expandedItem?.index === index) {
                            setExpandedItem(null);
                          } else {
                            setExpandedItem({ type: 'accommodation', index });
                          }
                        }}
                        onUpdate={(updateData) => updateItemByUniqueId('accommodation', acc.id ? acc.id.toString() : `acc_${index}`, updateData)}
                        onDelete={() => {}}
                      />
                      {/* Moved addons nested under this hotel */}
                      {hotelAddons.length > 0 && (
                        <div className="ml-6 border-l-4 border-amber-300 pl-3 space-y-2 mt-2 mb-2">
                          {hotelAddons.map((addon) => {
                            const addonUid = (addon as any).unique_id || addon.id?.toString();
                            const addonIdx = details.accommodation_cost_items.indexOf(addon);
                            const isExpanded = expandedItem?.type === 'accommodation' && expandedItem?.index === addonIdx;
                            return (
                              <div key={addonUid} className="border border-amber-200 rounded-lg bg-amber-50 p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded">{(addon as any).source_type || 'ADDON'}</span>
                                      <span className="font-medium text-sm text-gray-900 truncate">{addon.room_type}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
                                        addon.booking_status === 'BOOKED' ? 'bg-blue-100 text-blue-700' :
                                        addon.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>{addon.booking_status || 'NOT_BOOKED'}</span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      Cost: <strong>IDR {(addon.cost || 0).toLocaleString()}</strong>
                                      {addon.vendor && <span> · Vendor: <strong>{addon.vendor}</strong></span>}
                                    </div>
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0">
                                    <button onClick={() => moveItemFromAccommodation(addon)} className="p-1.5 text-orange-600 hover:bg-orange-100 border border-gray-200 rounded" title="Move back to Prebooked">
                                      <ArrowLeft className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => {
                                      if (isExpanded) setExpandedItem(null);
                                      else setExpandedItem({ type: 'accommodation', index: addonIdx });
                                    }} className="p-1.5 hover:bg-amber-100 border border-gray-200 rounded">
                                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                    </button>
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div className="mt-3 pt-3 border-t border-amber-200">
                                    {addon.booking_status === 'PAYMENT_COMPLETED' && (
                                      <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs font-medium text-green-800">
                                        🔒 Locked (Payment Completed). No changes allowed.
                                      </div>
                                    )}
                                    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                      <Select value={addon.booking_status || 'NOT_BOOKED'} onChange={(e) => {
                                        if (addon.booking_status === 'PAYMENT_COMPLETED') {
                                          alert('🔒 Payment Completed items are locked and cannot be changed.');
                                          return;
                                        }
                                        const newStatus = e.target.value;
                                        if (newStatus === 'PAYMENT_COMPLETED' && addon.vendor !== 'Wynder Ops') {
                                          alert('Payment Completed status can only be set for Wynder Ops vendor.');
                                          return;
                                        }
                                        const updateData: any = { booking_status: newStatus };
                                        if (newStatus === 'BOOKED' || newStatus === 'NOT_BOOKED') {
                                          updateData.due_date = '';
                                        }
                                        updateItemByUniqueId('accommodation', addonUid || '', updateData);
                                      }} className={`w-full text-sm ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed opacity-50 pointer-events-none' : ''}`}>
                                        <option value="NOT_BOOKED">Not Booked</option>
                                        <option value="BOOKED">Booked</option>
                                        <option value="PENDING_PAYMENT">Pending Payment</option>
                                        <option value="PAYMENT_COMPLETED">Payment Completed</option>
                                        <option value="CANCELED_NO_FEE">Canceled (No Fee)</option>
                                        <option value="CANCELED_WITH_FEE">Canceled (With Fee)</option>
                                      </Select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Cost (IDR)</label>
                                      <Input type="number" value={addon.cost || 0} onChange={(e) => {
                                        if (addon.booking_status === 'PAYMENT_COMPLETED') {
                                          alert('🔒 Payment Completed items are locked. Cost cannot be changed.');
                                          return;
                                        }
                                        const v = parseFloat(e.target.value) || 0; updateItemByUniqueId('accommodation', addonUid || '', { cost: v, estimated_amount: v });
                                      }} className={`w-full ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={addon.booking_status === 'PAYMENT_COMPLETED'} />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
                                      <VendorDropdown value={addon.vendor || ''} onChange={(v) => {
                                        if (addon.booking_status === 'PAYMENT_COMPLETED') {
                                          alert('🔒 Payment Completed items are locked. Vendor cannot be changed.');
                                          return;
                                        }
                                        updateItemByUniqueId('accommodation', addonUid || '', { vendor: v, vendor_name: v });
                                      }} placeholder="Select vendor" />
                                    </div>
                                    {(addon.booking_status === 'PENDING_PAYMENT' || addon.booking_status === 'PAYMENT_COMPLETED' || addon.booking_status === 'CANCELED_WITH_FEE') && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Payment Due Date *</label>
                                        <Input type="date" value={addon.due_date || ''} onChange={(e) => {
                                          if (addon.booking_status === 'PAYMENT_COMPLETED') return;
                                          updateItemByUniqueId('accommodation', addonUid || '', { due_date: e.target.value });
                                        }} className={`w-full ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={addon.booking_status === 'PAYMENT_COMPLETED'} />
                                      </div>
                                    )}
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Booking Reference</label>
                                      <Input value={addon.booking_reference || ''} onChange={(e) => {
                                        if (addon.booking_status === 'PAYMENT_COMPLETED') return;
                                        updateItemByUniqueId('accommodation', addonUid || '', { booking_reference: e.target.value });
                                      }} className={`w-full ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={addon.booking_status === 'PAYMENT_COMPLETED'} />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                                      <textarea value={addon.remarks || ''} onChange={(e) => {
                                        if (addon.booking_status === 'PAYMENT_COMPLETED') return;
                                        updateItemByUniqueId('accommodation', addonUid || '', { remarks: e.target.value });
                                      }} rows={2} className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={addon.booking_status === 'PAYMENT_COMPLETED'} />
                                    </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Accommodation-tagged addons from day_wise_details */}
                      {accommodationAddons.length > 0 && (
                        <div className="ml-6 border-l-4 border-blue-300 pl-3 space-y-2 mt-2 mb-2">
                          <div className="text-xs font-semibold text-blue-700">🏨 Accommodation Addons ({accommodationAddons.length})</div>
                          {accommodationAddons.map((addon, ai) => (
                            <div key={ai} className="flex justify-between items-center p-2 bg-blue-50 rounded border border-blue-200 text-sm">
                              <div>
                                <span className="font-medium text-gray-900">{addon.name}</span>
                                <span className="ml-2 text-xs text-gray-500">Day {addon.day || addon.day_number}</span>
                              </div>
                              <span className="font-medium text-gray-700">IDR {(addon.cost || 0).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              <FileUploadSection 
                sectionName="accommodation"
                itineraryId={itineraryId}
                vendors={Array.from(new Set(details.accommodation_cost_items.map(item => item.hotel_name).filter((v): v is string => Boolean(v))))}
                vendorType="accommodation"
                isAdmin={isAdmin}
              />
              
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-xl font-bold text-center text-blue-800">
                  💰 Total Accommodation Cost: IDR {details.accommodation_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {!reportOnly && activeTab === 'prebooked' && (
            <div className="p-3 sm:p-4 pb-16 sm:pb-12">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">🎫 Prebooked Activities & Meals</h3>
              </div>
              
              {/* Reconciliation Banner */}
              {reconciliation?.needs_reconciliation && !reconciliation.cancellation_mode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm mb-4">
                  <div className="font-medium text-amber-800 mb-1">⚠️ Previous version ({reconciliation.previous_version?.wyn_id}) had bookings</div>
                  <div className="text-amber-700 text-xs">
                    {reconciliation.summary.matched} matched · {reconciliation.summary.prev_only} removed · {reconciliation.summary.current_only} new
                  </div>
                </div>
              )}
              
              {/* Group items by day */}
              {(() => {
                const itemsByDay = details.prebooked_cost_items.reduce((acc, item) => {
                  const day = item.day || item.day_number || 0;
                  if (!acc[day]) acc[day] = [];
                  acc[day].push(item);
                  return acc;
                }, {} as { [key: number]: ActivityItem[] });
                
                const sortedDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);
                
                return sortedDays.map(day => (
                  <div key={day} className="mb-6 border border-gray-200 rounded-lg">
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-800">
                        {day === 0 ? '📋 General Items' : `📅 Day ${day}`}
                        <span className="ml-2 text-sm font-normal text-gray-600">({itemsByDay[day].length} items)</span>
                      </h4>
                      <Button variant="secondary" size="sm" className="text-xs" onClick={() => addNewItem('prebooked', day)}>
                        + Add Item
                      </Button>
                    </div>
                    <div className="p-3 space-y-3">
                      {itemsByDay[day].map((activity: ActivityItem) => {
                        // Find matching previous booking by activity_id or name
                        const prevMatch = reconciliation?.matched?.find((m: any) => {
                          if ((activity as any).activity_id && m.current?.activity_id) {
                            return m.current.activity_id === (activity as any).activity_id;
                          }
                          return m.current?.name?.toLowerCase() === activity.name?.toLowerCase();
                        });
                        const prevItem = prevMatch?.prev;
                        const isNotBooked = !activity.booking_status || activity.booking_status === 'NOT_BOOKED';
                        
                        return (
                          <div key={activity.unique_id}>
                            {prevItem && prevItem.status !== 'NOT_BOOKED' && !dismissedReconciliations.has(`pre_${prevItem.id}`) && (
                              <div className="mb-2 border border-indigo-200 rounded-lg overflow-hidden">
                                <div className="p-2.5 bg-indigo-50 flex items-center justify-between">
                                  <span className="text-xs font-medium text-indigo-700">
                                    📌 Previously booked in {reconciliation.previous_version?.wyn_id}
                                  </span>
                                  <div className="flex gap-2">
                                    {isNotBooked && (
                                      <button
                                        onClick={async () => {
                                          try {
                                            await api.post(`/booking-reconciliation/${itineraryId}/carry-forward`, {
                                              prev_item_id: prevItem.id,
                                              current_item_id: activity.id
                                            });
                                            loadedRef.current = false;
                                            loadDetails();
                                            api.get(`/booking-reconciliation/${itineraryId}`).then(res => {
                                              if (res.data.success && res.data.needs_reconciliation) setReconciliation(res.data);
                                              else setReconciliation(null);
                                            });
                                          } catch (err) { console.error(err); }
                                        }}
                                        className="px-2 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                      >
                                        Copy from previous
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setDismissedReconciliations(prev => new Set(prev).add(`pre_${prevItem.id}`))}
                                      className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                      ✅ Done
                                    </button>
                                  </div>
                                </div>
                                <div className="p-2.5 bg-white">
                                  <div className="grid grid-cols-2 gap-3 text-xs">
                                    {/* Previous */}
                                    <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                                      <div className="font-semibold text-red-800 mb-1.5">Previous ({reconciliation.previous_version?.wyn_id})</div>
                                      <div className="space-y-0.5 text-gray-700">
                                        <div>Day: <strong>{prevItem.day_number || '—'}</strong></div>
                                        {prevItem.activity_date && <div>Date: <strong>{new Date(prevItem.activity_date).toLocaleDateString('en-GB')}</strong></div>}
                                        <div>Type: <strong>{prevItem.item_type}</strong></div>
                                        <div>Cost: <strong>{prevItem.estimated_amount?.toLocaleString()}</strong></div>
                                        {prevItem.actual_amount > 0 && <div>Actual: <strong>{prevItem.actual_amount?.toLocaleString()}</strong></div>}
                                        <div className="pt-1 border-t border-red-200 mt-1">
                                          <div>Vendor: <strong className="text-blue-700">{prevItem.vendor_name || '—'}</strong></div>
                                          <div>Status: <strong>{prevItem.status}</strong></div>
                                          {prevItem.booking_reference && <div>Ref: <strong>{prevItem.booking_reference}</strong></div>}
                                          {prevItem.invoice_number && <div>Invoice: <strong>{prevItem.invoice_number}</strong></div>}
                                          {prevItem.payment_date && <div>Paid: <strong>{new Date(prevItem.payment_date).toLocaleDateString('en-GB')}</strong></div>}
                                        </div>
                                        {reconciliation.prev_vendor_files?.filter((f: any) => f.vendor_name === prevItem.vendor_name).length > 0 && (
                                          <div className="pt-1 border-t border-red-200 mt-1">
                                            <div className="font-medium text-gray-600">📎 Files:</div>
                                            {reconciliation.prev_vendor_files.filter((f: any) => f.vendor_name === prevItem.vendor_name).map((f: any) => (
                                              <div key={f.id} className="text-blue-600 truncate">{f.file_name} ({f.file_type})</div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Current */}
                                    <div className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                      <div className="font-semibold text-blue-800 mb-1.5">Current (WH_{itineraryId})</div>
                                      <div className="space-y-0.5 text-gray-700">
                                        <div>Day: <strong>{activity.day || activity.day_number || '—'}</strong></div>
                                        {activity.activity_date && <div>Date: <strong>{new Date(activity.activity_date).toLocaleDateString('en-GB')}</strong></div>}
                                        <div>Type: <strong>{(activity as any).item_type || 'prebooked'}</strong></div>
                                        <div>Cost: <strong>{(activity.cost || activity.estimated_amount)?.toLocaleString()}</strong></div>
                                        <div className="pt-1 border-t border-blue-200 mt-1">
                                          <div>Vendor: <strong>{activity.vendor || '—'}</strong></div>
                                          <div>Status: <strong>{activity.booking_status || 'NOT_BOOKED'}</strong></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {prevMatch?.has_differences && prevMatch.differences?.length > 0 && (
                                    <div className="mt-2 p-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                                      Δ Changes: {prevMatch.differences.join(' · ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <ActivityCard
                              item={activity}
                              type="prebooked"
                              masterData={masterData}
                              expanded={expandedItem?.type === 'prebooked' && expandedItem?.uniqueId === activity.unique_id}
                              onToggleExpand={() => {
                                if (expandedItem?.type === 'prebooked' && expandedItem?.uniqueId === activity.unique_id) {
                                  setExpandedItem(null);
                                } else {
                                  setExpandedItem({ type: 'prebooked', uniqueId: activity.unique_id });
                                }
                              }}
                              onUpdate={(updateData) => updateItemByUniqueId('prebooked', activity.id ? activity.id.toString() : activity.unique_id || `pre_${activity.name}`, updateData)}
                              onMove={() => moveItemBetweenSections(activity, 'prebooked')}
                              onMoveToAccommodation={() => moveItemToAccommodation(activity, 'prebooked')}
                              onDelete={() => deleteItem('prebooked', activity.unique_id || '')}
                              onQuickBook={() => handleQuickBook(activity)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
              
              <FileUploadSection 
                sectionName="prebooked_activities"
                itineraryId={itineraryId}
                vendors={Array.from(new Set(details.prebooked_cost_items.map(item => item.vendor).filter((v): v is string => Boolean(v))))}
                vendorType="prebooked"
                isAdmin={isAdmin}
              />
              
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-xl font-bold text-center text-green-800">
                  💰 Total Prebooked Cost: IDR {details.prebooked_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {!reportOnly && activeTab === 'driver_paid' && (
            <div className="p-3 sm:p-4 pb-16 sm:pb-12">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">💰 Driver Paid Activities</h3>
              </div>
              
              {/* Group items by day */}
              {(() => {
                const itemsByDay = details.driver_paid_cost_items.reduce((acc, item) => {
                  const day = item.day || item.day_number || 0;
                  if (!acc[day]) acc[day] = [];
                  acc[day].push(item);
                  return acc;
                }, {} as { [key: number]: ActivityItem[] });
                
                const sortedDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);
                
                return sortedDays.map(day => (
                  <div key={day} className="mb-6 border border-gray-200 rounded-lg">
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-800">
                        {day === 0 ? '📋 General Items' : `📅 Day ${day}`}
                        <span className="ml-2 text-sm font-normal text-gray-600">({itemsByDay[day].length} items)</span>
                      </h4>
                      <Button variant="secondary" size="sm" className="text-xs" onClick={() => addNewItem('driver_paid', day)}>
                        + Add Item
                      </Button>
                    </div>
                    <div className="p-3 space-y-3">
                      {itemsByDay[day].map((activity: ActivityItem) => (
                        <ActivityCard
                          key={activity.unique_id}
                          item={activity}
                          type="driver_paid"
                          masterData={masterData}
                          expanded={expandedItem?.type === 'driver_paid' && expandedItem?.uniqueId === activity.unique_id}
                          onToggleExpand={() => {
                            if (expandedItem?.type === 'driver_paid' && expandedItem?.uniqueId === activity.unique_id) {
                              setExpandedItem(null);
                            } else {
                              setExpandedItem({ type: 'driver_paid', uniqueId: activity.unique_id });
                            }
                          }}
                          onUpdate={(updateData) => updateItemByUniqueId('driver_paid', activity.id ? activity.id.toString() : activity.unique_id || `drv_${activity.name}`, updateData)}
                          onMove={() => moveItemBetweenSections(activity, 'driver_paid')}
                          onMoveToAccommodation={() => moveItemToAccommodation(activity, 'driver_paid')}
                          onDelete={() => deleteItem('driver_paid', activity.unique_id || '')}
                        />
                      ))}
                    </div>
                  </div>
                ));
              })()}
              
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-xl font-bold text-center text-yellow-800">
                  💰 Total Driver Paid Cost: IDR {details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {!reportOnly && activeTab === 'transportation' && (
            <div className="p-3 sm:p-4 pb-16 sm:pb-12">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">🚗 Transportation</h3>
              </div>
              
              {/* Transportation Handler */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Driver Name</label>
                    <Input
                      type="text"
                      value={transportationHandledBy}
                      onChange={(e) => {
                        setTransportationHandledBy(e.target.value);
                        setHasChanges(true);
                      }}
                      placeholder="Enter driver name"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <Select 
                      value={transportationStatus} 
                      onChange={(e) => { 
                        setTransportationStatus(e.target.value);
                        setHasChanges(true);
                      }} 
                      className="w-full"
                    >
                      <option value="NOT_BOOKED">Not Booked</option>
                      <option value="BOOKED">Booked</option>
                      <option value="PENDING_PAYMENT">Pending Payment</option>
                      <option value="PAYMENT_COMPLETED">Payment Completed</option>
                      <option value="CANCELED_NO_FEE">Canceled (No Fee)</option>
                      <option value="CANCELED_WITH_FEE">Canceled (With Fee)</option>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Transportation Items */}
              {details.transportation_cost_items && details.transportation_cost_items.length > 0 ? (
                <div className="space-y-3">
                  {details.transportation_cost_items.map((transport, index) => (
                    <TransportationCard
                      key={index}
                      item={transport}
                      index={index}
                      expanded={expandedItem?.type === 'transportation' && expandedItem?.index === index}
                      onToggleExpand={() => {
                        if (expandedItem?.type === 'transportation' && expandedItem?.index === index) {
                          setExpandedItem(null);
                        } else {
                          setExpandedItem({ type: 'transportation', index });
                        }
                      }}
                      onUpdate={(updateData) => {
                        const newDetails = { ...details };
                        newDetails.transportation_cost_items[index] = { 
                          ...newDetails.transportation_cost_items[index], 
                          ...updateData,
                          hasChanges: true // Mark as having changes
                        };
                        setDetails(newDetails);
                        setHasChanges(true);
                      }}
                      details={details}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-4xl mb-2">🚗</div>
                  <p className="font-medium">No transportation items found</p>
                  <p className="text-sm mt-1">Transportation costs are managed through the operational cost system</p>
                </div>
              )}
              
              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-xl font-bold text-center text-purple-800">
                  💰 Total Transportation Cost: IDR {(details.transportation_cost_items || []).reduce((sum, item) => {
                    const tripCost = item.trip_cost || 0;
                    const parking = (item as any).parking || 0;
                    const overtime = (item as any).overtime || 0;
                    return sum + tripCost + parking + overtime;
                  }, 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {!reportOnly && activeTab === 'report' && (
            <ReportTab details={details} itineraryId={itineraryId} onMarkComplete={markOperationComplete} />
          )}
        </div>

        {/* Hotel Picker Modal */}
        {hotelPickerState && details && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">🏨 Select Hotel</h3>
                <p className="text-sm text-gray-500 mt-1">Move "{hotelPickerState.item.name}" to which accommodation?</p>
              </div>
              <div className="p-4 space-y-2 max-h-60 overflow-y-auto">
                {details.accommodation_cost_items.map((acc, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      const state = hotelPickerState;
                      setHotelPickerState(null);
                      executeAccommodationMove(state.item, state.fromType, acc.hotel_name);
                    }}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{acc.hotel_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(acc.checkin_date)} → {formatDate(acc.checkout_date)} · {acc.no_of_nights} nights
                    </div>
                  </button>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-gray-200">
                <button onClick={() => setHotelPickerState(null)} className="w-full py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Save Button */}
        {!reportOnly && (
          <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
            <div className="flex justify-end">
              <Button variant="primary" onClick={saveAllChanges} disabled={saving || !hasChanges} className="w-full sm:w-auto">
                {saving ? 'Saving...' : hasChanges ? 'Save All Changes' : 'All Changes Saved'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Accommodation Card Component
interface AccommodationCardProps {
  item: AccommodationItem;
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (data: any) => void;
  onDelete: () => void;
  onMoveBack?: () => void;
}

const AccommodationCard: React.FC<AccommodationCardProps> = ({ item, expanded, onToggleExpand, onUpdate, onDelete, onMoveBack }) => {
  const [formData, setFormData] = useState({
    booking_status: item.booking_status || 'NOT_BOOKED',
    vendor_name: item.vendor || '',
    booking_reference: item.booking_reference || '',
    remarks: item.remarks || '',
    due_date: item.due_date || ''
  });

  // Update formData when item changes
  useEffect(() => {
    setFormData({
      booking_status: item.booking_status || 'NOT_BOOKED',
      vendor_name: item.vendor || '',
      booking_reference: item.booking_reference || '',
      remarks: item.remarks || '',
      due_date: item.due_date || ''
    });
  }, [item.booking_status, item.vendor, item.booking_reference, item.remarks, item.due_date]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'BOOKED':
      case 'PENDING_PAYMENT':
      case 'PAYMENT_COMPLETED':
        return <Check className="w-4 h-4 text-green-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'BOOKED':
        return 'bg-blue-100 text-blue-700';
      case 'PENDING_PAYMENT':
        return 'bg-yellow-100 text-yellow-700';
      case 'PAYMENT_COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'CANCELED_NO_FEE':
        return 'bg-red-50 text-red-600';
      case 'CANCELED_WITH_FEE':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {getStatusIcon(item.booking_status)}
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{item.hotel_name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.booking_status)} whitespace-nowrap ${item.booking_status === 'PAYMENT_COMPLETED' ? 'relative' : ''}`}>
              {item.booking_status === 'PAYMENT_COMPLETED' && <span className="mr-1">🔒</span>}
              {item.booking_status || 'NOT_BOOKED'}
            </span>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-1">
              <div>Room: <strong>{item.room_type}</strong></div>
              <div>Check-in: <strong>{formatDate(item.checkin_date)}</strong></div>
              <div>Check-out: <strong>{formatDate(item.checkout_date)}</strong></div>
              <div>Nights: <strong>{item.no_of_nights}</strong></div>
              <div>Rooms: <strong>{item.no_of_rooms}</strong></div>
              <div>Extra beds: <strong>{item.no_of_extra_beds || 0}</strong></div>
              <div>Kids BF: <strong>{(item as any).no_of_kids_for_breakfast || (item as any).calculated_kids || 0}</strong></div>
              <div>Cost: <strong>{(item.cost || item.estimated_amount || 0).toLocaleString()}</strong></div>
              {item.vendor && <div>Vendor: <strong className="text-blue-700">{item.vendor}</strong></div>}
              {item.booking_reference && <div>Ref: <strong>{item.booking_reference}</strong></div>}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 flex-shrink-0">
          {onMoveBack && (
            <button onClick={onMoveBack} className="p-2 text-orange-600 hover:bg-orange-50 border border-gray-200 rounded-lg" title="Move back to Prebooked">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={onToggleExpand} className="p-2">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button variant="secondary" size="sm" onClick={onDelete} className="text-red-600 hover:bg-red-50 p-2">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {item.booking_status === 'PAYMENT_COMPLETED' && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-medium text-green-800">
              🔒 This item is locked (Payment Completed). No changes allowed.
            </div>
          )}
          <div className={item.booking_status === 'PAYMENT_COMPLETED' ? 'opacity-50 pointer-events-none' : ''}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select 
                value={formData.booking_status} 
                onChange={(e) => { 
                  const newStatus = e.target.value;
                  
                  // Only restrict NEW changes to Payment Completed for non-Wynder Ops vendors
                  if (newStatus === 'PAYMENT_COMPLETED' && formData.vendor_name !== 'Wynder Ops' && item.booking_status !== 'PAYMENT_COMPLETED') {
                    alert('Payment Completed status can only be set for Wynder Ops vendor.');
                    return;
                  }
                  
                  // Clear due_date when status changes to BOOKED or NOT_BOOKED
                  const updateData: any = { booking_status: newStatus };
                  if (newStatus === 'BOOKED' || newStatus === 'NOT_BOOKED') {
                    updateData.due_date = '';
                    setFormData({ ...formData, booking_status: newStatus, due_date: '' });
                  } else {
                    setFormData({ ...formData, booking_status: newStatus });
                  }
                  onUpdate(updateData); 
                }} 
                className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed opacity-50 pointer-events-none' : ''}`}>
                <option value="NOT_BOOKED">Not Booked</option>
                <option value="BOOKED">Booked</option>
                <option value="PENDING_PAYMENT">Pending Payment</option>
                <option value="PAYMENT_COMPLETED">Payment Completed</option>
                <option value="CANCELED_NO_FEE">Canceled (No Fee)</option>
                <option value="CANCELED_WITH_FEE">Canceled (With Fee)</option>
              </Select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Cost (IDR)</label>
              <Input
                type="number"
                value={item.cost || item.estimated_amount || 0}
                onChange={(e) => {
                  if (item.booking_status === 'PAYMENT_COMPLETED') {
                    alert('🔒 Payment Completed items are locked. Cost cannot be changed.');
                    return;
                  }
                  const newCost = parseFloat(e.target.value) || 0;
                  onUpdate({ cost: newCost, estimated_amount: newCost });
                }}
                className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`}
                disabled={item.booking_status === 'PAYMENT_COMPLETED'}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Vendor *</label>
              <VendorDropdown
                value={formData.vendor_name}
                onChange={(value) => { 
                  // 🔒 STATUS LOCK: Prevent vendor changes for PAYMENT_COMPLETED items
                  if (item.booking_status === 'PAYMENT_COMPLETED') {
                    alert('🔒 Payment Completed items are locked. Vendor cannot be changed. Manual DB update required.');
                    return;
                  }
                  
                  // Only warn if changing vendor away from Wynder Ops for items that are currently Payment Completed
                  if (value !== 'Wynder Ops' && formData.booking_status === 'PAYMENT_COMPLETED') {
                    const confirmed = window.confirm('This item has Payment Completed status but vendor is being changed away from Wynder Ops. This may affect payment tracking. Continue?');
                    if (!confirmed) return;
                  }
                  setFormData({ ...formData, vendor_name: value }); 
                  onUpdate({ vendor: value, vendor_name: value }); 
                }}
                placeholder="Select or enter vendor"
              />
            </div>
            {(formData.booking_status === 'PENDING_PAYMENT' || formData.booking_status === 'PAYMENT_COMPLETED' || formData.booking_status === 'CANCELED_WITH_FEE') && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Due Date *</label>
                <Input 
                  type="date" 
                  value={formData.due_date} 
                  onChange={(e) => { 
                    if (item.booking_status === 'PAYMENT_COMPLETED') return;
                    setFormData({ ...formData, due_date: e.target.value }); 
                    onUpdate({ due_date: e.target.value }); 
                  }} 
                  className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`}
                  disabled={item.booking_status === 'PAYMENT_COMPLETED'}
                />
              </div>
            )}
          </div>
          <div className="mt-3">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Booking Reference</label>
            <Input value={formData.booking_reference} onChange={(e) => { 
              if (item.booking_status === 'PAYMENT_COMPLETED') return;
              setFormData({ ...formData, booking_reference: e.target.value }); onUpdate({ booking_reference: e.target.value }); 
            }} placeholder="Reference" className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={item.booking_status === 'PAYMENT_COMPLETED'} />
          </div>
          <div className="mt-3">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea value={formData.remarks} onChange={(e) => { 
              if (item.booking_status === 'PAYMENT_COMPLETED') return;
              setFormData({ ...formData, remarks: e.target.value }); onUpdate({ remarks: e.target.value }); 
            }} placeholder="Notes..." rows={2} className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`} disabled={item.booking_status === 'PAYMENT_COMPLETED'} />
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Activity Card Component
interface ActivityCardProps {
  item: ActivityItem;
  type: string;
  masterData: { activities: { [key: string]: number | { activity_id: number; activity_key: string; activity_name: string; type: string; status: string } }; meals: { [key: string]: number } };
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (data: any) => void;
  onMove: () => void;
  onMoveToAccommodation?: () => void;
  onDelete: () => void;
  onQuickBook?: () => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ item, type, masterData, expanded, onToggleExpand, onUpdate, onMove, onMoveToAccommodation, onDelete, onQuickBook }) => {
  const [formData, setFormData] = useState({
    booking_status: item.booking_status || 'NOT_BOOKED',
    vendor_name: item.vendor || '',
    due_date: item.due_date || ''
  });

  // Update formData when item changes
  useEffect(() => {
    setFormData({
      booking_status: item.booking_status || 'NOT_BOOKED',
      vendor_name: item.vendor || '',
      due_date: item.due_date || ''
    });
  }, [item.booking_status, item.vendor, item.due_date]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'BOOKED':
      case 'PENDING_PAYMENT':
      case 'PAYMENT_COMPLETED':
        return <Check className="w-4 h-4 text-green-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'BOOKED':
        return 'bg-blue-100 text-blue-700';
      case 'PENDING_PAYMENT':
        return 'bg-yellow-100 text-yellow-700';
      case 'PAYMENT_COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'CANCELED_NO_FEE':
        return 'bg-red-50 text-red-600';
      case 'CANCELED_WITH_FEE':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const isNoShow = item.verification_status === 'NO_SHOW';

  return (
    <div className={`border rounded-lg p-3 sm:p-4 mb-3 ${isNoShow ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
      {isNoShow && (
        <div className="mb-3 px-3 py-2 bg-red-100 border border-red-300 rounded-lg text-sm font-semibold text-red-800 flex items-center gap-2">
          ⚠️ NO SHOW — Please cancel this item and set amount to 0
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {getStatusIcon(item.booking_status)}
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{item.name || 'New Item'}</h3>
            {type !== 'driver_paid' && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.booking_status)} whitespace-nowrap ${item.booking_status === 'PAYMENT_COMPLETED' ? 'relative' : ''}`}>
                {item.booking_status === 'PAYMENT_COMPLETED' && <span className="mr-1">🔒</span>}
                {item.booking_status || 'NOT_BOOKED'}
              </span>
            )}
            <span className={`px-2 py-1 text-xs rounded whitespace-nowrap ${type === 'driver_paid' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}>
              {type === 'driver_paid' ? 'Driver Paid' : item.type}
            </span>
          </div>
          <div className="text-xs sm:text-sm text-gray-600 space-y-1">
            {item.day > 0 && <p>Day {item.day}{item.date ? ` • ${formatDate(item.date)}` : ''}</p>}
            {item.day === 0 && <p>General Item</p>}
            <p>Cost: {item.cost}</p>
            {((item.calculated_adults || 0) > 0 || (item.calculated_kids || 0) > 0) && (
              <p>Pax: {item.calculated_adults || 0} adults + {item.calculated_kids || 0} kids</p>
            )}
            {item.vendor && <p className="truncate">Vendor: {item.vendor}</p>}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 flex-shrink-0">
          {onQuickBook && item.vendor && !/watersports.*entrance|entrance.*watersports/i.test(item.name) && (
            <button onClick={onQuickBook} className="p-2 text-green-600 hover:bg-green-50 border border-gray-200 rounded-lg" title="Quick Book via WhatsApp">
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={onMove} className={`p-2 ${type === 'driver_paid' ? 'text-blue-600 hover:bg-blue-50' : 'text-orange-600 hover:bg-orange-50'}`}>
            {type === 'driver_paid' ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          </Button>
          {onMoveToAccommodation && (
            <button onClick={onMoveToAccommodation} className="p-2 text-blue-600 hover:bg-blue-50 border border-gray-200 rounded-lg" title="Move to Accommodation tab">
              🏨
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={onToggleExpand} className="p-2">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button variant="secondary" size="sm" onClick={onDelete} className="text-red-600 hover:bg-red-50 p-2">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {item.booking_status === 'PAYMENT_COMPLETED' && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-medium text-green-800">
              🔒 This item is locked (Payment Completed). No changes allowed.
            </div>
          )}
          <div className={item.booking_status === 'PAYMENT_COMPLETED' ? 'opacity-50 pointer-events-none' : ''}>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Type</label>
              <Select value={item.type || 'activity'} onChange={(e) => onUpdate({ type: e.target.value, item_subtype: e.target.value })} className="w-full">
                <option value="activity">Activity</option>
                <option value="meal">Meal</option>
                <option value="addon">Addon</option>
              </Select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                {item.type === 'activity' ? 'Activity' : item.type === 'meal' ? 'Meal' : 'Custom Item'}
              </label>
              {item.type === 'activity' ? (
                <SearchableSelect
                  options={[
                    ...(item.activity_id && !Object.values(masterData.activities).some(act => 
                      typeof act === 'object' ? act.activity_id === item.activity_id : act === item.activity_id
                    ) 
                      ? [{ value: item.activity_id.toString(), label: `${item.name} (ID: ${item.activity_id} - Not Found)` }] 
                      : []),
                    ...Object.entries(masterData.activities).map(([key, activityData]) => {
                      if (typeof activityData === 'object') {
                        const statusIndicator = activityData.status === 'DISABLED' ? ' (DISABLED)' : '';
                        return { 
                          value: activityData.activity_id.toString(), 
                          label: `${key} (${activityData.type || 'REGULAR'})${statusIndicator}` 
                        };
                      } else {
                        return { value: activityData.toString(), label: key };
                      }
                    })
                  ]}
                  value={item.activity_id ? item.activity_id.toString() : ''}
                  onChange={(selectedId) => {
                    const selectedEntry = Object.entries(masterData.activities).find(([key, activityData]) => 
                      typeof activityData === 'object' ? activityData.activity_id === Number(selectedId) : activityData === Number(selectedId)
                    );
                    const selectedKey = selectedEntry ? selectedEntry[0] : item.name || '';
                    onUpdate({ activity_id: Number(selectedId), name: selectedKey });
                  }}
                  placeholder="Select Activity"
                  className="w-full"
                />
              ) : item.type === 'meal' ? (
                <SearchableSelect
                  options={Object.entries(masterData.meals).map(([key, id]) => ({ value: id.toString(), label: key }))}
                  value={item.meal_id ? item.meal_id.toString() : ''}
                  onChange={(selectedId) => {
                    const selectedKey = Object.keys(masterData.meals).find(key => masterData.meals[key] === Number(selectedId)) || '';
                    onUpdate({ meal_id: Number(selectedId), name: selectedKey });
                  }}
                  placeholder="Select Meal"
                  className="w-full"
                />
              ) : (
                <Input
                  type="text"
                  value={item.name || ''}
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  placeholder="Enter custom item"
                  className="w-full"
                />
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Cost (IDR)</label>
              <Input
                type="number"
                value={item.cost || item.estimated_amount || 0}
                onChange={(e) => {
                  // 🔒 STATUS LOCK: Prevent cost changes for PAYMENT_COMPLETED items
                  if (item.booking_status === 'PAYMENT_COMPLETED') {
                    alert('🔒 Payment Completed items are locked. Cost cannot be changed. Manual DB update required.');
                    return;
                  }
                  const newCost = parseFloat(e.target.value) || 0;
                  onUpdate({ cost: newCost, estimated_amount: newCost });
                }}
                className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`}
                disabled={item.booking_status === 'PAYMENT_COMPLETED'}
              />
            </div>
            {type !== 'driver_paid' && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select 
                  value={formData.booking_status} 
                  onChange={(e) => { 
                    const newStatus = e.target.value;
                    
                    // 🔒 STATUS LOCK: Prevent any changes from PAYMENT_COMPLETED
                    if (item.booking_status === 'PAYMENT_COMPLETED') {
                      alert('🔒 Payment Completed items are locked and cannot be changed. Manual DB update required.');
                      return;
                    }
                    
                    // Only restrict NEW changes to Payment Completed for non-Wynder Ops vendors
                    if (newStatus === 'PAYMENT_COMPLETED' && formData.vendor_name !== 'Wynder Ops' && item.booking_status !== 'PAYMENT_COMPLETED') {
                      alert('Payment Completed status can only be set for Wynder Ops vendor.');
                      return;
                    }
                    
                    // Clear due_date when status changes to BOOKED or NOT_BOOKED
                    const updateData: any = { booking_status: newStatus };
                    if (newStatus === 'BOOKED' || newStatus === 'NOT_BOOKED') {
                      updateData.due_date = '';
                      setFormData({ ...formData, booking_status: newStatus, due_date: '' });
                    } else {
                      setFormData({ ...formData, booking_status: newStatus });
                    }
                    onUpdate(updateData); 
                  }} 
                  className={`w-full ${item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-50 border-green-300 cursor-not-allowed opacity-50 pointer-events-none' : ''}`}>
                  <option value="NOT_BOOKED">Not Booked</option>
                  <option value="BOOKED">Booked</option>
                  <option value="PENDING_PAYMENT">Pending Payment</option>
                  <option value="PAYMENT_COMPLETED">Payment Completed</option>
                  <option value="CANCELED_NO_FEE">Canceled (No Fee)</option>
                  <option value="CANCELED_WITH_FEE">Canceled (With Fee)</option>
                </Select>
              </div>
            )}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Vendor *</label>
              <VendorDropdown
                value={formData.vendor_name}
                onChange={(value) => { 
                  // 🔒 STATUS LOCK: Prevent vendor changes for PAYMENT_COMPLETED items
                  if (item.booking_status === 'PAYMENT_COMPLETED') {
                    alert('🔒 Payment Completed items are locked. Vendor cannot be changed. Manual DB update required.');
                    return;
                  }
                  
                  // Only warn if changing vendor away from Wynder Ops for items that are currently Payment Completed
                  if (value !== 'Wynder Ops' && formData.booking_status === 'PAYMENT_COMPLETED') {
                    const confirmed = window.confirm('This item has Payment Completed status but vendor is being changed away from Wynder Ops. This may affect payment tracking. Continue?');
                    if (!confirmed) return;
                  }
                  setFormData({ ...formData, vendor_name: value }); 
                  onUpdate({ vendor: value }); 
                }}
                placeholder="Select or enter vendor"
              />
            </div>
            {(formData.booking_status === 'PENDING_PAYMENT' || formData.booking_status === 'PAYMENT_COMPLETED' || formData.booking_status === 'CANCELED_WITH_FEE') && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Due Date *</label>
                <Input 
                  type="date" 
                  value={formData.due_date} 
                  onChange={(e) => { 
                    setFormData({ ...formData, due_date: e.target.value }); 
                    onUpdate({ due_date: e.target.value }); 
                  }} 
                  className="w-full" 
                />
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

// File Upload Section Component (Memoized to prevent unnecessary re-renders)
interface FileUploadSectionProps {
  sectionName: string;
  itineraryId: number;
  vendors?: string[];
  vendorType?: 'accommodation' | 'prebooked';
  isAdmin?: boolean;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = React.memo(({ sectionName, itineraryId, vendors, vendorType, isAdmin = false }) => {
  const [selectedVendor, setSelectedVendor] = useState<string>('ALL_VENDORS');
  const [selectedFileType, setSelectedFileType] = useState<'invoice' | 'receipt'>('invoice');
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [vendorFiles, setVendorFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [viewerState, setViewerState] = useState<{ isOpen: boolean; fileUrl: string; fileName: string; fileType: 'pdf' | 'image' }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '', 
    fileType: 'image' 
  });

  const handleFileEdit = async (fileKey: string, newFileType: 'receipt' | 'invoice') => {
    try {
      await api.put(`/operational-cost/${itineraryId}/files/${fileKey}`, {
        file_type: newFileType === 'invoice' ? 'operational' : 'receipt'
      });
      await loadFiles();
      setEditingFile(null);
    } catch (error: any) {
      console.error('Error updating file type:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error updating file type: ${errorMsg}. Please try again.`);
    }
  };

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      if (selectedVendor === 'ALL_VENDORS') {
        // Load all files and group by vendor_id
        const response = await api.get(`/operational-cost/${itineraryId}/files/${sectionName}`);
        if (response.data.success) {
          const allFiles = response.data.data || [];
          // Group files by vendor_id (not vendor_name)
          const grouped = allFiles.reduce((acc: any, file: any) => {
            const vendorId = file.vendor_id || 'unknown';
            const vendorName = file.vendor_name || file.vendor || 'Unknown Vendor';
            if (!acc[vendorId]) {
              acc[vendorId] = { vendor_id: vendorId, vendor_name: vendorName, receipts: [], invoices: [] };
            }
            if (file.source === 'receipt') {
              acc[vendorId].receipts.push(file);
            } else {
              acc[vendorId].invoices.push(file);
            }
            return acc;
          }, {});
          setVendorFiles(Object.values(grouped));
        } else {
          setVendorFiles([]);
        }
        setFiles([]);
      } else {
        const url = selectedVendor
          ? `/operational-cost/${itineraryId}/files/${sectionName}?vendor=${encodeURIComponent(selectedVendor)}`
          : `/operational-cost/${itineraryId}/files/${sectionName}`;
        const response = await api.get(url);
        if (response.data.success) {
          setFiles(response.data.data || []);
        } else {
          setFiles([]);
        }
        setVendorFiles([]);
      }
    } catch (error: any) {
      console.error('Error loading files:', error);
      setFiles([]);
      setVendorFiles([]);
    } finally {
      setLoading(false);
    }
  }, [itineraryId, sectionName, selectedVendor]);

  // Debounce file loading to prevent rapid successive calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadFiles();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [itineraryId, sectionName, selectedVendor]);

  const handleDeleteFile = async (fileKey: string, fileName: string) => {
    if (!isAdmin) {
      alert('❌ Only administrators can delete files');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await api.delete(`/operational-cost/${itineraryId}/files/${encodeURIComponent(fileKey)}`);
      if (response.data.success) {
        alert(`✅ File "${fileName}" deleted successfully`);
        await loadFiles();
      } else {
        alert(`❌ Failed to delete file: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert(`❌ Error deleting file: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    if (!selectedVendor) {
      alert('Please select a vendor first');
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(selectedFiles)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('section', sectionName);
        formData.append('file_type', selectedFileType === 'invoice' ? 'operational' : 'receipt');
        
        const uploadUrl = `/operational-cost/${itineraryId}/upload?vendor=${encodeURIComponent(selectedVendor)}`;
        
        await api.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      await loadFiles();
      event.target.value = '';
    } catch (error: any) {
      console.error('Error uploading files:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error uploading files: ${errorMsg}. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = (fileUrl: string, fileName: string) => {
    console.log('Attempting to view file:', { fileUrl, fileName });
    const fileExt = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt || '')) {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'image' });
    } else if (fileExt === 'pdf') {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'pdf' });
    } else {
      // For other file types, try direct download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    }
  };

  const handleFileDelete = async (file: any) => {
    if (!window.confirm('Are you sure you want to delete this file? This will remove it from both the database and cloud storage.')) return;
    
    try {
      console.log('File object for deletion:', file);
      
      // Try different possible ID fields
      const fileId = file.id || file.file_id || file.database_id;
      if (fileId) {
        const response = await api.delete(`/operational-cost/${itineraryId}/files/id/${fileId}`);
        if (response.data.success) {
          await loadFiles();
          return;
        }
      }
      
      // Fallback to original file key approach
      const fileKey = file.key || file.s3_key || file.file_key;
      if (fileKey) {
        const response = await api.delete(`/operational-cost/${itineraryId}/files/${encodeURIComponent(fileKey)}`);
        if (response.data.success) {
          await loadFiles();
          return;
        }
      }
      
      throw new Error('No valid file identifier found');
    } catch (error: any) {
      console.error('Error deleting file:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error deleting file: ${errorMsg}. Check console for details.`);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext || '')) {
      return '📃';
    }
    return '📎';
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case 'receipt':
        return 'bg-green-100 text-green-700';
      case 'operational':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="mt-6 sm:mt-8 border-t-2 border-gray-200 pt-4 sm:pt-6 bg-gray-50 rounded-lg p-3 sm:p-4">
      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        📎 {sectionName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Documents
        <span className="text-sm font-normal text-gray-500">({files.length} files)</span>
      </h4>
      
      {/* Vendor and File Type Selection */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Select Vendor *</label>
          <SearchableSelect
            options={[
              { value: 'ALL_VENDORS', label: 'All Vendors' },
              ...(vendors?.map(vendor => ({ value: vendor, label: vendor })) || [])
            ]}
            value={selectedVendor}
            onChange={(value) => setSelectedVendor(value.toString())}
            placeholder="Choose vendor"
            className="w-full"
          />
        </div>
        {selectedVendor !== 'ALL_VENDORS' && (
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">File Type *</label>
            <Select
              value={selectedFileType}
              onChange={(e) => setSelectedFileType(e.target.value as 'invoice' | 'receipt')}
              className="w-full"
            >
              <option value="invoice">Invoice (Operational)</option>
            </Select>
          </div>
        )}
      </div>

      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white rounded-lg border-2 border-dashed border-blue-300">
        {selectedVendor !== 'ALL_VENDORS' && (
          <>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">
              📄 Upload {selectedFileType} files {selectedVendor ? `for ${selectedVendor}` : ''}
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                disabled={uploading || !selectedVendor || selectedVendor === 'ALL_VENDORS'}
                className="block w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-3 file:px-3 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 cursor-pointer"
              />
              {uploading && (
                <div className="flex items-center gap-2 text-blue-600 mt-2 sm:mt-0">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-xs sm:text-sm">Uploading...</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 flex flex-col sm:flex-row sm:items-center gap-1">
              📎 Supported formats: JPG, PNG, PDF, DOC, DOCX (Max 10MB per file)
              {(!selectedVendor || selectedVendor === 'ALL_VENDORS') && <span className="text-red-600 font-medium">• Please select a specific vendor first</span>}
            </p>
          </>
        )}
      </div>
      
      {/* Files List */}
      <div className="space-y-3 bg-white rounded-lg p-4 border">
        <h5 className="font-medium text-gray-900">
          {sectionName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Documents:
        </h5>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading files...</span>
          </div>
        ) : selectedVendor === 'ALL_VENDORS' ? (
          // Show all vendors in containers
          vendorFiles.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-4xl mb-2">📁</div>
              <p className="font-medium">No vendor files found</p>
              <p className="text-xs mt-1">Upload files by selecting specific vendors</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {vendorFiles.map((vendorGroup, index) => (
                <div key={index} className="bg-gray-50 border rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3">
                    {vendorGroup.vendor_name || vendorGroup.vendor || 'Unknown Vendor'}
                    <span className="ml-2 text-sm font-normal text-gray-500">({(vendorGroup.receipts?.length || 0) + (vendorGroup.invoices?.length || 0)} files)</span>
                  </h6>
                  
                  {/* Receipts */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-green-700 mb-2 flex items-center">
                      <span className="text-green-600 mr-1">🧾</span>
                      Receipts ({vendorGroup.receipts?.length || 0})
                    </div>
                    {vendorGroup.receipts?.length > 0 ? (
                      <div className="space-y-1">
                        {vendorGroup.receipts.map((file: any, fileIndex: number) => (
                          <div key={fileIndex} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                            <span className="truncate">{file.name || file.filename}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleViewFile(file.url || file.downloadUrl || file.s3_url, file.name || file.filename)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                title="Preview"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                              <a
                                href={file.downloadUrl || file.url || file.s3_url}
                                download={file.name || file.filename}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </a>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteFile(file.key, file.name || file.filename)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                  title="Delete (Admin only)"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No receipts uploaded</p>
                    )}
                  </div>
                  
                  {/* Invoices */}
                  <div>
                    <div className="text-sm font-medium text-blue-700 mb-2 flex items-center">
                      <span className="text-blue-600 mr-1">📄</span>
                      Invoices ({vendorGroup.invoices?.length || 0})
                    </div>
                    {vendorGroup.invoices?.length > 0 ? (
                      <div className="space-y-1">
                        {vendorGroup.invoices.map((file: any, fileIndex: number) => (
                          <div key={fileIndex} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                            <span className="truncate">{file.name || file.filename}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleViewFile(file.url || file.downloadUrl || file.s3_url, file.name || file.filename)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                title="Preview"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                              <a
                                href={file.downloadUrl || file.url || file.s3_url}
                                download={file.name || file.filename}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </a>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteFile(file.key, file.name || file.filename)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                  title="Delete (Admin only)"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No invoices uploaded</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : files.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">📁</div>
            <p className="font-medium">No {sectionName.replace('_', ' ')} documents uploaded yet</p>
            <p className="text-xs mt-1">Upload files using the form above</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-2xl">{getFileIcon(file.name)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getFileTypeColor(file.source || 'operational')}`}>
                        {file.source === 'receipt' ? 'Receipt' : 'Invoice'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {file.vendor && <span className="font-medium">{file.vendor} • </span>}
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''} • 
                      {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Unknown date'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const fileUrl = file.url || file.downloadUrl;
                      if (fileUrl) {
                        handleViewFile(fileUrl, file.name);
                      } else {
                        alert('File URL not available for preview');
                      }
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Preview file"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  <a
                    href={file.downloadUrl || file.url}
                    download={file.name}
                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                    title="Download file"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  
                  <button
                    onClick={() => setEditingFile(file.key || '')}
                    className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                    title="Edit file type"
                  >
                    ✏️
                  </button>
                  
                  <button
                    onClick={() => handleFileDelete(file)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    title="Delete file"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <FileViewerModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        fileUrl={viewerState.fileUrl}
        fileName={viewerState.fileName}
        fileType={viewerState.fileType}
      />
    </div>
  );
});

// Transportation Card Component
interface TransportationCardProps {
  item: TransportationItem & { parking?: number; overtime?: number; remarks?: string };
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (data: any) => void;
  details: BookingDetails;
}

const TransportationCard: React.FC<TransportationCardProps> = ({ item, expanded, onToggleExpand, onUpdate, details }) => {
  const [formData, setFormData] = useState({
    trip: item.trip || '',
    trip_cost: item.trip_cost || 0,
    parking: item.parking || 0,
    overtime: item.overtime || 0,
    remarks: item.remarks || ''
  });

  useEffect(() => {
    setFormData({
      trip: item.trip || '',
      trip_cost: item.trip_cost || 0,
      parking: item.parking || 0,
      overtime: item.overtime || 0,
      remarks: item.remarks || ''
    });
  }, [item]);

  const totalCost = (formData.trip_cost || 0) + (formData.parking || 0) + (formData.overtime || 0);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-gray-900">
            Day {item.day} - {(item.trip || 'Transportation').replace(/^Day \d+ - /, '')}
          </h4>
          {(() => {
            const dayActivities = [
              ...details.prebooked_cost_items.filter(act => act.day === item.day).map(act => `${act.name}(P)`),
              ...details.driver_paid_cost_items.filter(act => act.day === item.day).map(act => `${act.name}(D)`)
            ];
            return dayActivities.length > 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-2 text-sm text-blue-800">{dayActivities.join('. ')}</div>
            ) : null;
          })()}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{formatDate(item.date)}</span>
          <Button variant="secondary" size="sm" onClick={onToggleExpand} className="p-2">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      
      {expanded ? (
        <div className="space-y-3">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip Name</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-2 rounded border whitespace-nowrap">Day {item.day} -</span>
              <input
                type="text"
                value={formData.trip.replace(/^Day \d+ - /, '')}
                onChange={(e) => {
                  const newTrip = `Day ${item.day} - ${e.target.value}`;
                  setFormData({ ...formData, trip: newTrip });
                  onUpdate({ trip: newTrip });
                }}
                placeholder="Arrival [Kuta/Seminyak/Legian] | Uluwatu Tour"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip Cost (IDR)</label>
              <Input
                type="number"
                value={formData.trip_cost}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value) || 0;
                  setFormData({ ...formData, trip_cost: newValue });
                  onUpdate({ trip_cost: newValue });
                }}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parking (IDR)</label>
              <Input
                type="number"
                value={formData.parking}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value) || 0;
                  setFormData({ ...formData, parking: newValue });
                  onUpdate({ parking: newValue });
                }}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overtime (IDR)</label>
              <Input
                type="number"
                value={formData.overtime}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value) || 0;
                  setFormData({ ...formData, overtime: newValue });
                  onUpdate({ overtime: newValue });
                }}
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => {
                setFormData({ ...formData, remarks: e.target.value });
                onUpdate({ remarks: e.target.value });
              }}
              placeholder="Transportation remarks..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">Total Day Cost:</span>
              <span className="font-bold text-purple-600">IDR {totalCost.toLocaleString()}</span>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
            <div className="bg-gray-50 rounded p-2">
              <span className="text-gray-600">Trip Cost:</span>
              <span className="ml-2 font-medium">IDR {formData.trip_cost?.toLocaleString() || 0}</span>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <span className="text-gray-600">Parking:</span>
              <span className="ml-2 font-medium">IDR {formData.parking?.toLocaleString() || 0}</span>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <span className="text-gray-600">Overtime:</span>
              <span className="ml-2 font-medium">IDR {formData.overtime?.toLocaleString() || 0}</span>
            </div>
          </div>
          {formData.remarks && (
            <div className="text-sm text-gray-600 mb-3">
              <span className="font-medium">Remarks:</span> {formData.remarks}
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-900">Total Day Cost:</span>
            <span className="font-bold text-purple-600">IDR {totalCost.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Transportation Section Component
interface TransportationSectionProps {
  title: string;
  items: TransportationItem[];
  total: number;
  bgColor: string;
  driverName: string;
}

const TransportationSection: React.FC<TransportationSectionProps> = ({ title, items, total, bgColor, driverName }) => {
  const [expanded, setExpanded] = useState(false);

  const totalTripCost = items.reduce((sum, item) => sum + (item.trip_cost || 0), 0);
  const totalParking = items.reduce((sum, item) => sum + ((item as any).parking || 0), 0);
  const totalOvertime = items.reduce((sum, item) => sum + ((item as any).overtime || 0), 0);

  return (
    <div className="border-b border-gray-200">
      <div 
        className={`${bgColor} px-4 py-3 cursor-pointer hover:bg-opacity-80 transition-colors`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">{title}</span>
            <span className="text-sm text-gray-600">({items.length} items)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-bold text-gray-900">IDR {total.toLocaleString()}</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 py-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-700">Day</th>
                <th className="text-left py-2 font-medium text-gray-700">Trip</th>
                <th className="text-right py-2 font-medium text-gray-700">Trip Cost</th>
                <th className="text-right py-2 font-medium text-gray-700">Parking</th>
                <th className="text-right py-2 font-medium text-gray-700">Overtime</th>
                <th className="text-right py-2 font-medium text-gray-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const tripCost = item.trip_cost || 0;
                const parking = (item as any).parking || 0;
                const overtime = (item as any).overtime || 0;
                const itemTotal = tripCost + parking + overtime;
                
                return (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-2 text-center font-medium text-gray-900">{item.day}</td>
                    <td className="py-2 text-gray-900">{item.trip}</td>
                    <td className="py-2 text-right">IDR {tripCost.toLocaleString()}</td>
                    <td className="py-2 text-right">IDR {parking.toLocaleString()}</td>
                    <td className="py-2 text-right">IDR {overtime.toLocaleString()}</td>
                    <td className="py-2 text-right font-medium">IDR {itemTotal.toLocaleString()}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-300 bg-gray-50">
                <td className="py-2 font-bold text-gray-800" colSpan={2}>TOTALS</td>
                <td className="py-2 text-right font-bold">IDR {totalTripCost.toLocaleString()}</td>
                <td className="py-2 text-right font-bold">IDR {totalParking.toLocaleString()}</td>
                <td className="py-2 text-right font-bold">IDR {totalOvertime.toLocaleString()}</td>
                <td className="py-2 text-right font-bold">IDR {total.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Expense Section Component
interface ExpenseSectionProps {
  title: string;
  items: Array<{
    name: string;
    details: string;
    cost: number;
    vendor: string;
    status: string;
  }>;
  total: number;
  bgColor: string;
}

const ExpenseSection: React.FC<ExpenseSectionProps> = ({ title, items, total, bgColor }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'BOOKED': return 'bg-blue-100 text-blue-700';
      case 'PENDING_PAYMENT': return 'bg-yellow-100 text-yellow-700';
      case 'PAYMENT_COMPLETED': return 'bg-green-100 text-green-700';
      case 'DRIVER_PAID': return 'bg-orange-100 text-orange-700';
      case 'TRANSPORT': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="border-b border-gray-200">
      <div 
        className={`${bgColor} px-4 py-3 cursor-pointer hover:bg-opacity-80 transition-colors`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">{title}</span>
            <span className="text-sm text-gray-600">({items.length} items)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-bold text-gray-900">IDR {total.toLocaleString()}</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 py-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-700">Item</th>
                <th className="text-left py-2 font-medium text-gray-700">Details</th>
                <th className="text-left py-2 font-medium text-gray-700">Vendor</th>
                <th className="text-center py-2 font-medium text-gray-700">Status</th>
                <th className="text-right py-2 font-medium text-gray-700">Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 font-medium text-gray-900">{item.name}</td>
                  <td className="py-2 text-gray-600">{item.details}</td>
                  <td className="py-2 text-gray-600">{item.vendor}</td>
                  <td className="py-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-2 text-right font-medium">IDR {item.cost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Report Tab Component
interface ReportTabProps {
  details: BookingDetails;
  itineraryId: number;
  onMarkComplete: () => void;
}

const ReportTab: React.FC<ReportTabProps> = ({ details, itineraryId, onMarkComplete }) => {
  const [downloading, setDownloading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter items based on selected filters
  const getFilteredItems = () => {
    const allItems = [
      ...details.accommodation_cost_items.map(item => ({ ...item, section: 'Accommodation' })),
      ...details.prebooked_cost_items.map(item => ({ ...item, section: 'Prebooked' })),
      ...details.driver_paid_cost_items.map(item => ({ ...item, section: 'Driver Paid' }))
    ];

    return allItems.filter(item => {
      const typeMatch = typeFilter === 'all' || item.section.toLowerCase() === typeFilter.toLowerCase();
      const statusMatch = statusFilter === 'all' || item.booking_status === statusFilter;
      return typeMatch && statusMatch;
    });
  };

  const downloadExcelReport = async () => {
    try {
      setDownloading(true);
      
      // Use xlsx library to create and download
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      
      // Create Summary Sheet
      const summaryData = createSummaryData(details);
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      
      // Create Accommodations Sheet
      const accommodationData = createAccommodationData(details.accommodation_cost_items);
      const accommodationSheet = XLSX.utils.aoa_to_sheet(accommodationData);
      XLSX.utils.book_append_sheet(wb, accommodationSheet, 'Accommodations');
      
      // Create Activities Sheet
      const activitiesData = createActivitiesData(details.prebooked_cost_items, details.driver_paid_cost_items);
      const activitiesSheet = XLSX.utils.aoa_to_sheet(activitiesData);
      XLSX.utils.book_append_sheet(wb, activitiesSheet, 'Activities');
      
      // Create Transportation Sheet
      const transportationData = createTransportationData(details.transportation_cost_items || []);
      const transportationSheet = XLSX.utils.aoa_to_sheet(transportationData);
      XLSX.utils.book_append_sheet(wb, transportationSheet, 'Transportation');
      
      XLSX.writeFile(wb, `WH_${itineraryId}_Booking_Report.xlsx`);
    } catch (error: any) {
      console.error('Error downloading Excel report:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error generating Excel report: ${errorMsg}. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };

  const downloadSimpleReport = async () => {
    try {
      // Fetch payment history for driver (vendor ID 840)
      let driverPayments = [];
      let driverOutstanding = 0;
      let driverTotalPaid = 0;
      
      try {
        const paymentResponse = await api.get(`/accounts-v3/payment-history/840/${itineraryId}`);
        if (paymentResponse.data.success) {
          driverPayments = paymentResponse.data.data || [];
          driverTotalPaid = driverPayments.reduce((sum: number, p: any) => sum + (p.payment_amount || 0), 0);
          
          // Calculate outstanding amount
          const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
          const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
            const tripCost = item.trip_cost || 0;
            const parking = (item as any).parking || 0;
            const overtime = (item as any).overtime || 0;
            return sum + tripCost + parking + overtime;
          }, 0);
          const totalDriverAmount = driverPaidTotal + transportationTotal;
          driverOutstanding = totalDriverAmount - driverTotalPaid;
        }
      } catch (error) {
        console.error('Error fetching driver payments:', error);
      }
      
      const htmlContent = generateSimpleReport(details, driverPayments, driverTotalPaid, driverOutstanding);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const guestName = details.lead_guest_name ? details.lead_guest_name.replace(/^(Mr|Mrs|Ms|Dr)\.?\s+/i, '').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 20) : 'Guest';
      link.download = `${guestName}.html`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error generating driver report:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error generating driver report: ${errorMsg}. Please try again.`);
    }
  };

  const downloadExpensesReport = () => {
    const htmlContent = generateExpensesReport(details, accommodationTotal, prebookedTotal, driverPaidTotal, transportationTotal, actualTotal);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `WH_${itineraryId}_All_Expenses_Report.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const generateExpensesReport = (details: BookingDetails, accommodationTotal: number, prebookedTotal: number, driverPaidTotal: number, transportationTotal: number, actualTotal: number) => {
    const transportItems = details.transportation_cost_items || [];
    const totalTripCost = transportItems.reduce((sum, item) => sum + (item.trip_cost || 0), 0);
    const totalParking = transportItems.reduce((sum, item) => sum + ((item as any).parking || 0), 0);
    const totalOvertime = transportItems.reduce((sum, item) => sum + ((item as any).overtime || 0), 0);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>WH_${details.itinerary_id} - All Expenses Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { background: #4a5568; color: white; padding: 15px; text-align: center; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f8f9fa; font-weight: bold; }
        .section-header { background: #e9ecef; font-weight: bold; }
        .total-row { background: #d4edda; font-weight: bold; }
        .grand-total { background: #28a745; color: white; font-weight: bold; }
        .text-right { text-align: right; }
    </style>
</head>
<body>
    <div class="header">
        <h1>📊 All Expenses Report</h1>
        <h2>WH_${details.itinerary_id} - ${details.lead_guest_name || 'Unknown'}</h2>
        <p>${details.travel_date ? formatDate(details.travel_date) : 'N/A'} | ${details.no_of_pax || 0} adults + ${details.no_of_children || 0} kids</p>
    </div>

    <h3>🏨 Accommodation (${details.accommodation_cost_items.length} items)</h3>
    <table>
        <tr><th>Hotel</th><th>Room Details</th><th>Vendor</th><th>Status</th><th class="text-right">Cost</th></tr>
        ${details.accommodation_cost_items.map(item => `
        <tr>
            <td>${item.hotel_name}</td>
            <td>${item.room_type} • ${item.no_of_nights} nights • ${item.no_of_rooms} rooms</td>
            <td>${item.vendor || '-'}</td>
            <td>${item.booking_status || 'NOT_BOOKED'}</td>
            <td class="text-right">IDR ${(item.cost || 0).toLocaleString()}</td>
        </tr>
        `).join('')}
        <tr class="total-row"><td colspan="4">ACCOMMODATION TOTAL</td><td class="text-right">IDR ${accommodationTotal.toLocaleString()}</td></tr>
    </table>

    <h3>🎫 Prebooked (${details.prebooked_cost_items.length} items)</h3>
    <table>
        <tr><th>Activity</th><th>Details</th><th>Vendor</th><th>Status</th><th class="text-right">Cost</th></tr>
        ${details.prebooked_cost_items.map(item => `
        <tr>
            <td>${item.name}</td>
            <td>Day ${item.day || 0} • ${item.type || 'activity'}</td>
            <td>${item.vendor || '-'}</td>
            <td>${item.booking_status || 'NOT_BOOKED'}</td>
            <td class="text-right">IDR ${(item.cost || 0).toLocaleString()}</td>
        </tr>
        `).join('')}
        <tr class="total-row"><td colspan="4">PREBOOKED TOTAL</td><td class="text-right">IDR ${prebookedTotal.toLocaleString()}</td></tr>
    </table>

    <h3>💰 Driver Paid (${details.driver_paid_cost_items.length} items)</h3>
    <table>
        <tr><th>Activity</th><th>Details</th><th>Vendor</th><th class="text-right">Cost</th></tr>
        ${details.driver_paid_cost_items.map(item => `
        <tr>
            <td>${item.name}</td>
            <td>Day ${item.day || 0} • ${item.type || 'activity'}</td>
            <td>${item.vendor || 'Driver'}</td>
            <td class="text-right">IDR ${(item.cost || 0).toLocaleString()}</td>
        </tr>
        `).join('')}
        <tr class="total-row"><td colspan="3">DRIVER PAID TOTAL</td><td class="text-right">IDR ${driverPaidTotal.toLocaleString()}</td></tr>
    </table>

    <h3>🚗 Transportation (${transportItems.length} items)</h3>
    <table>
        <tr><th>Day</th><th>Trip</th><th class="text-right">Trip Cost</th><th class="text-right">Parking</th><th class="text-right">Overtime</th><th class="text-right">Total</th></tr>
        ${transportItems.map(item => {
          const tripCost = item.trip_cost || 0;
          const parking = (item as any).parking || 0;
          const overtime = (item as any).overtime || 0;
          const total = tripCost + parking + overtime;
          return `
        <tr>
            <td>${item.day}</td>
            <td>${item.trip}</td>
            <td class="text-right">IDR ${tripCost.toLocaleString()}</td>
            <td class="text-right">IDR ${parking.toLocaleString()}</td>
            <td class="text-right">IDR ${overtime.toLocaleString()}</td>
            <td class="text-right">IDR ${total.toLocaleString()}</td>
        </tr>
          `;
        }).join('')}
        <tr class="total-row">
            <td colspan="2">TRANSPORTATION TOTAL</td>
            <td class="text-right">IDR ${totalTripCost.toLocaleString()}</td>
            <td class="text-right">IDR ${totalParking.toLocaleString()}</td>
            <td class="text-right">IDR ${totalOvertime.toLocaleString()}</td>
            <td class="text-right">IDR ${transportationTotal.toLocaleString()}</td>
        </tr>
    </table>

    <table>
        <tr class="grand-total"><td>💰 GRAND TOTAL (Driver Paid + Transportation)</td><td class="text-right">IDR ${(driverPaidTotal + transportationTotal).toLocaleString()}</td></tr>
        <tr class="grand-total"><td>💰 ALL EXPENSES TOTAL</td><td class="text-right">IDR ${actualTotal.toLocaleString()}</td></tr>
    </table>

    <p style="text-align: center; margin-top: 30px; color: #666;">Generated: ${new Date().toLocaleString()}</p>
</body>
</html>
    `;
  };

  const downloadHtmlReport = () => {
    const htmlContent = generateHtmlReport(details);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `WH_${itineraryId}_Booking_Report.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const generateSimpleReport = (details: BookingDetails, driverPayments: any[] = [], driverTotalPaid: number = 0, driverOutstanding: number = 0) => {
    const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
      const tripCost = item.trip_cost || 0;
      const parking = (item as any).parking || 0;
      const overtime = (item as any).overtime || 0;
      return sum + tripCost + parking + overtime;
    }, 0);
    const actualTotal = driverPaidTotal + transportationTotal;

    // Group activities by day
    const itemsByDay = [...details.prebooked_cost_items, ...details.driver_paid_cost_items].reduce((acc, item) => {
      const day = item.day || item.day_number || 0;
      if (!acc[day]) acc[day] = { prebooked: [], driver_paid: [] };
      if (details.prebooked_cost_items.includes(item as any)) {
        acc[day].prebooked.push(item);
      } else {
        acc[day].driver_paid.push(item);
      }
      return acc;
    }, {} as { [key: number]: { prebooked: any[], driver_paid: any[] } });

    // Add days with transportation but no activities
    if (details.transportation_cost_items) {
      details.transportation_cost_items.forEach((transport: any) => {
        const day = transport.day || 0;
        if (!itemsByDay[day]) itemsByDay[day] = { prebooked: [], driver_paid: [] };
      });
    }

    const sortedDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>WH_${details.itinerary_id} - Transport Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 10px; font-size: 12px; line-height: 1.4; }
        .header { background: #4a5568; color: white; padding: 10px; text-align: center; margin-bottom: 15px; }
        .summary { background: #f7fafc; padding: 10px; margin-bottom: 15px; border: 1px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
        th, td { border: 1px solid #cbd5e0; padding: 8px; text-align: left; vertical-align: top; }
        th { background: #edf2f7; font-weight: bold; }
        .day-header { background: #718096; color: white; font-weight: bold; text-align: center; }
        .prebooked { background: #f0fff4; }
        .driver-paid { background: #fffbf0; }
        .transport { background: #f5f3ff; }
        .total-row { background: #e6fffa; font-weight: bold; }
        .cost { text-align: right; }
        .voucher { text-align: center; font-style: italic; color: #718096; }
    </style>
</head>
<body>
    <div class="header">
        <h2>WH_${details.itinerary_id} - Transport Report</h2>
        <div>${details.lead_guest_name || 'Unknown'}</div>
        <div>${details.travel_date ? formatDate(details.travel_date) : 'N/A'} | ${details.no_of_pax || 0} adults : ${details.no_of_children || 0} kids${details.children_ages && details.children_ages.length > 0 ? ` : Ages ${details.children_ages.join(', ')}` : ''}</div>
    </div>

    <div class="summary">
        <strong>Total Transport Cost: IDR ${actualTotal.toLocaleString()}</strong><br>
        Driver Paid: IDR ${driverPaidTotal.toLocaleString()} | 
        Transport: IDR ${transportationTotal.toLocaleString()}<br><br>
        <strong>Payment Summary:</strong><br>
        Total Paid to Driver: IDR ${driverTotalPaid.toLocaleString()}<br>
        Outstanding Amount: IDR ${driverOutstanding.toLocaleString()}
    </div>

    ${driverPayments.length > 0 ? `
    <div style="background: #e6fffa; padding: 10px; margin-bottom: 15px; border: 1px solid #4fd1c7;">
        <strong>Payment History (${driverPayments.length} payments):</strong><br>
        <table style="width: 100%; margin-top: 10px; font-size: 11px;">
            <tr style="background: #2d3748; color: white;">
                <td style="padding: 5px; border: 1px solid #fff; color: white; font-weight: bold;">Date</td>
                <td style="padding: 5px; border: 1px solid #fff; color: white; font-weight: bold;">Amount</td>
                <td style="padding: 5px; border: 1px solid #fff; color: white; font-weight: bold;">Reference</td>
                <td style="padding: 5px; border: 1px solid #fff; color: white; font-weight: bold;">Bank</td>
            </tr>
            ${driverPayments.map(payment => `
            <tr>
                <td style="padding: 5px; border: 1px solid #4fd1c7;">${payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : 'N/A'}</td>
                <td style="padding: 5px; border: 1px solid #4fd1c7; text-align: right;">IDR ${(payment.payment_amount || 0).toLocaleString()}</td>
                <td style="padding: 5px; border: 1px solid #4fd1c7;">${payment.payment_reference || '-'}</td>
                <td style="padding: 5px; border: 1px solid #4fd1c7;">${payment.bank_account || '-'}</td>
            </tr>
            `).join('')}
            <tr style="background: #b2f5ea; font-weight: bold;">
                <td style="padding: 5px; border: 1px solid #4fd1c7;">TOTAL</td>
                <td style="padding: 5px; border: 1px solid #4fd1c7; text-align: right;">IDR ${driverTotalPaid.toLocaleString()}</td>
                <td colspan="2" style="padding: 5px; border: 1px solid #4fd1c7; text-align: center;">${driverPayments.length} payment${driverPayments.length !== 1 ? 's' : ''}</td>
            </tr>
        </table>
    </div>
    ` : ''}

    ${sortedDays.map(day => {
      const dayTransportation = details.transportation_cost_items?.find(t => t.day === day);
      const dayDate = itemsByDay[day].prebooked[0]?.date || itemsByDay[day].driver_paid[0]?.date || dayTransportation?.date;
      return `
    <table id="day${day}">
        <tr><th colspan="3" class="day-header">DAY ${day}${dayDate ? ` - ${formatDate(dayDate)}` : ''}</th></tr>
        <tr><th style="width: 20%;">Type</th><th style="width: 60%;">Item</th><th style="width: 20%;">Cost</th></tr>
        ${itemsByDay[day].prebooked.map(item => `
        <tr class="prebooked">
            <td>Prebooked</td>
            <td>${item.name}</td>
            <td class="voucher">Voucher</td>
        </tr>
        `).join('')}
        ${itemsByDay[day].driver_paid.map(item => `
        <tr class="driver-paid">
            <td>Driver Paid</td>
            <td>${item.name}</td>
            <td class="cost">IDR ${(item.cost || 0).toLocaleString()}</td>
        </tr>
        `).join('')}
        ${dayTransportation ? `
        <tr class="transport">
            <td>Transport</td>
            <td>${dayTransportation.trip}<br><small>Base: ${(dayTransportation.trip_cost || 0).toLocaleString()}${((dayTransportation as any).parking || 0) > 0 ? ` | Parking: ${((dayTransportation as any).parking || 0).toLocaleString()}` : ''}${((dayTransportation as any).overtime || 0) > 0 ? ` | OT: ${((dayTransportation as any).overtime || 0).toLocaleString()}` : ''}</small></td>
            <td class="cost">IDR ${((dayTransportation.trip_cost || 0) + ((dayTransportation as any).parking || 0) + ((dayTransportation as any).overtime || 0)).toLocaleString()}</td>
        </tr>
        ` : ''}
        <tr class="total-row">
            <td colspan="2">DAY ${day} TOTAL</td>
            <td class="cost">IDR ${(itemsByDay[day].driver_paid.reduce((sum, item) => sum + (item.cost || 0), 0) + (dayTransportation ? (dayTransportation.trip_cost || 0) + ((dayTransportation as any).parking || 0) + ((dayTransportation as any).overtime || 0) : 0)).toLocaleString()}</td>
        </tr>
    </table>
      `;
    }).join('')}

    <table>
        <tr class="total-row"><td colspan="2">Driver Paid Activities</td><td class="cost">IDR ${driverPaidTotal.toLocaleString()}</td></tr>
        <tr class="total-row"><td colspan="2">Transportation Cost</td><td class="cost">IDR ${transportationTotal.toLocaleString()}</td></tr>
        <tr class="total-row"><td colspan="2" style="text-align: center; font-size: 14px;">TOTAL AMOUNT</td><td class="cost" style="font-size: 14px;">IDR ${actualTotal.toLocaleString()}</td></tr>
        ${driverPayments.length > 0 ? driverPayments.map(payment => `
        <tr style="background: #4fd1c7; color: white;">
            <td colspan="2" style="text-align: center; font-size: 14px;">${payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : 'N/A'}</td>
            <td class="cost" style="font-size: 14px;">IDR ${(payment.payment_amount || 0).toLocaleString()}</td>
        </tr>
        `).join('') : ''}
        ${driverPayments.length > 0 ? `<tr style="background: #2d3748; color: white;"><td colspan="2" style="text-align: center; font-size: 14px; font-weight: bold;">TOTAL PAID</td><td class="cost" style="font-size: 14px; font-weight: bold;">IDR ${driverTotalPaid.toLocaleString()}</td></tr>` : ''}
        <tr style="background: ${driverOutstanding > 0 ? '#fed7d7' : '#c6f6d5'}; color: ${driverOutstanding > 0 ? '#c53030' : '#2f855a'};"><td colspan="2" style="text-align: center; font-size: 14px; font-weight: bold;">OUTSTANDING</td><td class="cost" style="font-size: 14px; font-weight: bold;">IDR ${driverOutstanding.toLocaleString()}</td></tr>
    </table>

    <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #666;">
        Generated: ${new Date().toLocaleString()}
    </div>
</body>
</html>
    `;
  };

  const generateHtmlReport = (details: BookingDetails) => {
    const accommodationTotal = details.accommodation_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const prebookedTotal = details.prebooked_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
      const tripCost = item.trip_cost || 0;
      const parking = (item as any).parking || 0;
      const overtime = (item as any).overtime || 0;
      return sum + tripCost + parking + overtime;
    }, 0);
    const actualTotal = accommodationTotal + prebookedTotal + driverPaidTotal + transportationTotal;

    // Group activities by day
    const itemsByDay = [...details.prebooked_cost_items, ...details.driver_paid_cost_items].reduce((acc, item) => {
      const day = item.day || item.day_number || 0;
      if (!acc[day]) acc[day] = { prebooked: [], driver_paid: [] };
      if (details.prebooked_cost_items.includes(item as any)) {
        acc[day].prebooked.push(item);
      } else {
        acc[day].driver_paid.push(item);
      }
      return acc;
    }, {} as { [key: number]: { prebooked: any[], driver_paid: any[] } });

    const sortedDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Booking Report - WH_${details.itinerary_id}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 10px; margin-bottom: 20px; }
        .section { margin-bottom: 30px; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
        .section-header { background: #f8f9fa; padding: 15px; font-weight: bold; border-bottom: 1px solid #e0e0e0; }
        .section-content { padding: 15px; }
        .cost-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px; }
        .cost-card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; }
        .cost-amount { font-size: 1.5em; font-weight: bold; color: #2c3e50; }
        .item { background: #f9f9f9; margin: 10px 0; padding: 15px; border-radius: 5px; border-left: 4px solid #3498db; }
        .item-prebooked { border-left-color: #27ae60; }
        .item-driver { border-left-color: #f39c12; }
        .status { padding: 4px 8px; border-radius: 4px; font-size: 0.8em; font-weight: bold; }
        .status-booked { background: #d4edda; color: #155724; }
        .status-pending { background: #fff3cd; color: #856404; }
        .status-completed { background: #d1ecf1; color: #0c5460; }
        .status-not-booked { background: #f8d7da; color: #721c24; }
        .day-section { margin: 20px 0; border: 1px solid #dee2e6; border-radius: 8px; }
        .day-header { background: #e9ecef; padding: 10px 15px; font-weight: bold; }
        .accommodation-table, .transport-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .accommodation-table th, .accommodation-table td, .transport-table th, .transport-table td { 
            border: 1px solid #dee2e6; padding: 8px; text-align: left; 
        }
        .accommodation-table th, .transport-table th { background: #f8f9fa; font-weight: bold; }
        .total-row { background: #e8f5e8; font-weight: bold; }
        @media print { body { margin: 0; } .no-print { display: none; } }
    </style>
</head>
<body>
    <div class="header">
        <h1>🏖️ Booking Report - WH_${details.itinerary_id}</h1>
        <p><strong>Guest:</strong> ${details.lead_guest_name || 'Unknown'} | <strong>Agency:</strong> ${details.agency_name || 'Unknown'}</p>
        <p><strong>Travel Date:</strong> ${details.travel_date ? new Date(details.travel_date).toLocaleDateString() : 'N/A'} | <strong>Passengers:</strong> ${details.no_of_pax || 0} adults + ${details.no_of_children || 0} children</p>
    </div>

    <div class="section">
        <div class="section-header">💰 Cost Summary</div>
        <div class="section-content">
            <div class="cost-grid">
                <div class="cost-card">
                    <div>🏨 Accommodations</div>
                    <div class="cost-amount">IDR ${accommodationTotal.toLocaleString()}</div>
                    <div>${details.accommodation_cost_items.length} items</div>
                </div>
                <div class="cost-card">
                    <div>🎫 Prebooked</div>
                    <div class="cost-amount">IDR ${prebookedTotal.toLocaleString()}</div>
                    <div>${details.prebooked_cost_items.length} items</div>
                </div>
                <div class="cost-card">
                    <div>💰 Driver Paid</div>
                    <div class="cost-amount">IDR ${driverPaidTotal.toLocaleString()}</div>
                    <div>${details.driver_paid_cost_items.length} items</div>
                </div>
                <div class="cost-card">
                    <div>🚗 Transportation</div>
                    <div class="cost-amount">IDR ${transportationTotal.toLocaleString()}</div>
                    <div>${details.transportation_cost_items?.length || 0} items</div>
                </div>
            </div>
            <div style="text-align: center; font-size: 1.2em; font-weight: bold; color: #2c3e50; border-top: 2px solid #3498db; padding-top: 15px;">
                Total Operational Cost: IDR ${actualTotal.toLocaleString()}
            </div>
        </div>
    </div>

    <div class="section">
        <div class="section-header">🏨 Accommodation Details</div>
        <div class="section-content">
            <table class="accommodation-table">
                <thead>
                    <tr>
                        <th>Hotel Name</th>
                        <th>Room Details</th>
                        <th>Dates</th>
                        <th>Vendor</th>
                        <th>Cost (IDR)</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    ${details.accommodation_cost_items.map(item => `
                    <tr>
                        <td><strong>${item.hotel_name}</strong></td>
                        <td>${item.room_type}<br><small>${item.no_of_nights} nights • ${item.no_of_rooms} rooms</small></td>
                        <td>${formatDate(item.checkin_date)} - ${formatDate(item.checkout_date)}</td>
                        <td>${item.vendor || '-'}</td>
                        <td><strong>IDR ${(item.cost || 0).toLocaleString()}</strong></td>
                        <td><span class="status status-${item.booking_status?.toLowerCase().replace('_', '-') || 'not-booked'}">${item.booking_status || 'NOT_BOOKED'}</span></td>
                    </tr>
                    `).join('')}
                </tbody>
            </table>
        </div>
    </div>

    <div class="section">
        <div class="section-header">🎫 Activities by Day (${details.prebooked_cost_items.length + details.driver_paid_cost_items.length} items)</div>
        <div class="section-content">
            ${sortedDays.length === 0 ? '<p>No activities found</p>' : ''}
            ${sortedDays.map(day => {
              const dayTransportation = details.transportation_cost_items?.find(t => t.day === day);
              const dayDate = itemsByDay[day].prebooked[0]?.date || itemsByDay[day].driver_paid[0]?.date || dayTransportation?.date;
              return `
            <div class="day-section">
                <div class="day-header">
                    ${day === 0 ? '📋 General Items' : `📅 Day ${day}`}${dayDate ? ` - ${formatDate(dayDate)}` : ''} (${itemsByDay[day].prebooked.length + itemsByDay[day].driver_paid.length} items)
                </div>
                <div style="padding: 15px;">
                    ${itemsByDay[day].prebooked.length > 0 ? `
                    <h4 style="color: #27ae60; margin: 10px 0;">🎫 Prebooked Activities (${itemsByDay[day].prebooked.length})</h4>
                    ${itemsByDay[day].prebooked.map(item => `
                    <div class="item item-prebooked">
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>
                                <strong>${item.name}</strong><br>
                                <small>Vendor: ${item.vendor || 'N/A'} • Cost: IDR ${(item.cost || 0).toLocaleString()}</small>
                            </div>
                            <span class="status status-${item.booking_status?.toLowerCase().replace('_', '-') || 'not-booked'}">${item.booking_status || 'NOT_BOOKED'}</span>
                        </div>
                    </div>
                    `).join('')}
                    ` : ''}
                    
                    ${itemsByDay[day].driver_paid.length > 0 ? `
                    <h4 style="color: #f39c12; margin: 10px 0;">💰 Driver Paid Activities (${itemsByDay[day].driver_paid.length})</h4>
                    ${itemsByDay[day].driver_paid.map(item => `
                    <div class="item item-driver">
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>
                                <strong>${item.name}</strong><br>
                                <small>Vendor: ${item.vendor || 'Driver'} • Cost: IDR ${(item.cost || 0).toLocaleString()}</small>
                            </div>
                            <span class="status" style="background: #fff3cd; color: #856404;">Driver Paid</span>
                        </div>
                    </div>
                    `).join('')}
                    ` : ''}
                    
                    ${dayTransportation ? `
                    <h4 style="color: #8e44ad; margin: 10px 0;">🚗 Transportation</h4>
                    <div style="background: #f8f5ff; border: 1px solid #d1c4e9; border-radius: 5px; padding: 15px; margin: 10px 0;">
                        <strong>${dayTransportation.trip}</strong><br>
                        <table style="width: 100%; margin-top: 10px; border-collapse: collapse;">
                            <tr>
                                <th style="text-align: left; padding: 5px; border-bottom: 1px solid #ddd;">Base Cost (IDR)</th>
                                <th style="text-align: left; padding: 5px; border-bottom: 1px solid #ddd;">Parking (IDR)</th>
                                <th style="text-align: left; padding: 5px; border-bottom: 1px solid #ddd;">Overtime (IDR)</th>
                                <th style="text-align: left; padding: 5px; border-bottom: 1px solid #ddd;">Total (IDR)</th>
                            </tr>
                            <tr>
                                <td style="padding: 5px;">${(dayTransportation.trip_cost || 0).toLocaleString()}</td>
                                <td style="padding: 5px;">${((dayTransportation as any).parking || 0).toLocaleString()}</td>
                                <td style="padding: 5px;">${((dayTransportation as any).overtime || 0).toLocaleString()}</td>
                                <td style="padding: 5px; font-weight: bold;">${((dayTransportation.trip_cost || 0) + ((dayTransportation as any).parking || 0) + ((dayTransportation as any).overtime || 0)).toLocaleString()}</td>
                            </tr>
                        </table>
                    </div>
                    ` : ''}
                </div>
            </div>
            `;
            }).join('')}
        </div>
    </div>

    ${details.transportation_cost_items && details.transportation_cost_items.length > 0 ? `
    <div class="section">
        <div class="section-header">🚗 Transportation Details</div>
        <div class="section-content">
            <p><strong>Driver Name:</strong> ${details.transportation_handled_by || 'Not Assigned'}</p>
            <table class="transport-table">
                <thead>
                    <tr>
                        <th>Day</th>
                        <th>Trip</th>
                        <th>Base Cost (IDR)</th>
                        <th>Parking (IDR)</th>
                        <th>Overtime (IDR)</th>
                        <th>Total (IDR)</th>
                    </tr>
                </thead>
                <tbody>
                    ${details.transportation_cost_items.map(item => {
                      const tripCost = item.trip_cost || 0;
                      const parking = (item as any).parking || 0;
                      const overtime = (item as any).overtime || 0;
                      const total = tripCost + parking + overtime;
                      return `
                    <tr>
                        <td>${item.day}</td>
                        <td><strong>${item.trip}</strong></td>
                        <td>IDR ${tripCost.toLocaleString()}</td>
                        <td>IDR ${parking.toLocaleString()}</td>
                        <td>IDR ${overtime.toLocaleString()}</td>
                        <td><strong>IDR ${total.toLocaleString()}</strong></td>
                    </tr>
                      `;
                    }).join('')}
                    <tr class="total-row">
                        <td colspan="5"><strong>Total Transportation Cost</strong></td>
                        <td><strong>IDR ${transportationTotal.toLocaleString()}</strong></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    ` : ''}

    <div class="section">
        <div class="section-header">📊 Summary</div>
        <div class="section-content">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Operational Cost:</strong> IDR ${actualTotal.toLocaleString()}</p>
            <p><strong>Booking Progress:</strong> ${[...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'BOOKED').length} of ${details.accommodation_cost_items.length + details.prebooked_cost_items.length} items booked</p>
        </div>
    </div>
</body>
</html>
    `;
  };

  const createSummaryData = (details: BookingDetails) => {
    const accommodationTotal = details.accommodation_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const prebookedTotal = details.prebooked_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
    const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
      const tripCost = item.trip_cost || 0;
      const parking = (item as any).parking || 0;
      const overtime = (item as any).overtime || 0;
      return sum + tripCost + parking + overtime;
    }, 0);
    
    const data = [
      ['Booking Report Summary'],
      [''],
      ['Itinerary ID', `WH_${details.itinerary_id}`],
      ['Guest Name', details.lead_guest_name || 'Unknown'],
      ['Agency', details.agency_name || 'Unknown'],
      ['Travel Date', details.travel_date ? new Date(details.travel_date).toLocaleDateString() : 'N/A'],
      ['Passengers', `${details.no_of_pax || 0} adults + ${details.no_of_children || 0} children`],
      [''],
      ['Cost Breakdown (IDR)'],
      ['Accommodations', accommodationTotal.toLocaleString()],
      ['Prebooked Activities', prebookedTotal.toLocaleString()],
      ['Driver Paid Activities', driverPaidTotal.toLocaleString()],
      ['Transportation', transportationTotal.toLocaleString()],
      [''],
      ['Total Estimated Cost', (details.total_estimated_cost || 0).toLocaleString()],
      ['Total Actual Cost', (accommodationTotal + prebookedTotal + driverPaidTotal + transportationTotal).toLocaleString()],
      [''],
      ['Booking Status Breakdown'],
      ['Total Items', details.accommodation_cost_items.length + details.prebooked_cost_items.length + details.driver_paid_cost_items.length],
      ['Booked Items', [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'BOOKED').length],
      ['Pending Payment', [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'PENDING_PAYMENT').length],
      ['Not Booked', [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'NOT_BOOKED').length]
    ];
    
    return data;
  };

  const createAccommodationData = (items: AccommodationItem[]) => {
    const headers = ['Hotel Name', 'Room Type', 'Check-in', 'Check-out', 'Nights', 'Rooms', 'Cost (IDR)', 'Status', 'Vendor', 'Reference', 'Remarks'];
    const data = [headers, ...items.map(item => [
      item.hotel_name || '',
      item.room_type || '',
      item.checkin_date ? formatDate(item.checkin_date) : '',
      item.checkout_date ? formatDate(item.checkout_date) : '',
      item.no_of_nights || 0,
      item.no_of_rooms || 0,
      item.cost || 0,
      item.booking_status || 'NOT_BOOKED',
      item.vendor || '',
      item.booking_reference || '',
      item.remarks || ''
    ])];
    
    return data;
  };

  const createActivitiesData = (prebookedItems: ActivityItem[], driverPaidItems: ActivityItem[]) => {
    const headers = ['Type', 'Name', 'Day', 'Date', 'Cost (IDR)', 'Status', 'Vendor', 'Reference', 'Remarks'];
    const allItems = [
      ...prebookedItems.map(item => ({ ...item, section: 'Prebooked' })),
      ...driverPaidItems.map(item => ({ ...item, section: 'Driver Paid' }))
    ];
    
    const data = [headers, ...allItems.map(item => [
      item.section,
      item.name || '',
      item.day || 0,
      item.date ? formatDate(item.date) : '',
      item.cost || 0,
      item.booking_status || 'NOT_BOOKED',
      item.vendor || '',
      item.booking_reference || '',
      item.remarks || ''
    ])];
    
    return data;
  };

  const createTransportationData = (items: TransportationItem[]) => {
    const headers = ['Day', 'Date', 'Trip', 'Trip Cost (IDR)', 'Parking (IDR)', 'Overtime (IDR)', 'Total (IDR)', 'Remarks'];
    const data = [headers, ...items.map(item => {
      const tripCost = item.trip_cost || 0;
      const parking = (item as any).parking || 0;
      const overtime = (item as any).overtime || 0;
      const total = tripCost + parking + overtime;
      
      return [
        item.day || 0,
        item.date ? formatDate(item.date) : '',
        item.trip || '',
        tripCost,
        parking,
        overtime,
        total,
        (item as any).remarks || ''
      ];
    })];
    
    return data;
  };

  const accommodationTotal = details.accommodation_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
  const prebookedTotal = details.prebooked_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
  const driverPaidTotal = details.driver_paid_cost_items.reduce((sum, item) => sum + (item.cost || 0), 0);
  const transportationTotal = (details.transportation_cost_items || []).reduce((sum, item) => {
    const tripCost = item.trip_cost || 0;
    const parking = (item as any).parking || 0;
    const overtime = (item as any).overtime || 0;
    return sum + tripCost + parking + overtime;
  }, 0);
  const actualTotal = accommodationTotal + prebookedTotal + driverPaidTotal + transportationTotal;

  const totalItems = details.accommodation_cost_items.length + details.prebooked_cost_items.length + details.driver_paid_cost_items.length;
  const bookedItems = [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'BOOKED').length;
  const pendingPayment = [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'PENDING_PAYMENT').length;
  const notBooked = [...details.accommodation_cost_items, ...details.prebooked_cost_items].filter(item => item.booking_status === 'NOT_BOOKED').length;

  return (
    <div className="p-4 pb-16 space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">📊 Booking Report</h3>
        <div className="flex gap-2">
          <Button 
            onClick={downloadSimpleReport}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            Driver Report
          </Button>
          <Button 
            onClick={downloadHtmlReport}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            Download HTML
          </Button>
          <Button 
            onClick={onMarkComplete}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <CheckCircle className="w-4 h-4" />
            Mark Complete
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">🔍 Filters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <Select 
              value={typeFilter} 
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full"
            >
              <option value="all">All Types</option>
              <option value="accommodation">Accommodation</option>
              <option value="prebooked">Prebooked</option>
              <option value="driver paid">Driver Paid</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full"
            >
              <option value="all">All Statuses</option>
              <option value="NOT_BOOKED">Not Booked</option>
              <option value="BOOKED">Booked</option>
              <option value="PENDING_PAYMENT">Pending Payment</option>
              <option value="PAYMENT_COMPLETED">Payment Completed</option>
              <option value="CANCELED_NO_FEE">Canceled (No Fee)</option>
              <option value="CANCELED_WITH_FEE">Canceled (With Fee)</option>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">🏨 Accommodations</h4>
          <p className="text-2xl font-bold text-blue-900">IDR {accommodationTotal.toLocaleString()}</p>
          <p className="text-sm text-blue-600">{details.accommodation_cost_items.length} items</p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">🎫 Prebooked</h4>
          <p className="text-2xl font-bold text-green-900">IDR {prebookedTotal.toLocaleString()}</p>
          <p className="text-sm text-green-600">{details.prebooked_cost_items.length} items</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">💰 Driver Paid</h4>
          <p className="text-2xl font-bold text-yellow-900">IDR {driverPaidTotal.toLocaleString()}</p>
          <p className="text-sm text-yellow-600">{details.driver_paid_cost_items.length} items</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="font-semibold text-purple-800 mb-2">🚗 Transportation</h4>
          <p className="text-2xl font-bold text-purple-900">IDR {transportationTotal.toLocaleString()}</p>
          <p className="text-sm text-purple-600">{details.transportation_cost_items?.length || 0} items</p>
        </div>
      </div>

      {/* All Expenses Summary */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h4 className="text-lg font-semibold text-gray-900">📊 All Expenses Report</h4>
          <Button 
            onClick={downloadExpensesReport}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
          >
            <Download className="w-4 h-4" />
            Download HTML
          </Button>
        </div>
        
        {/* Accommodation Section */}
        <ExpenseSection 
          title="🏨 Accommodation" 
          items={details.accommodation_cost_items.map(item => ({
            name: item.hotel_name,
            details: `${item.room_type} • ${item.no_of_nights} nights • ${item.no_of_rooms} rooms`,
            cost: item.cost || 0,
            vendor: item.vendor || '-',
            status: item.booking_status || 'NOT_BOOKED'
          }))}
          total={accommodationTotal}
          bgColor="bg-blue-50"
        />
        
        {/* Prebooked Section */}
        <ExpenseSection 
          title="🎫 Prebooked" 
          items={details.prebooked_cost_items.map(item => ({
            name: item.name,
            details: `Day ${item.day || 0} • ${item.type || 'activity'}`,
            cost: item.cost || 0,
            vendor: item.vendor || '-',
            status: item.booking_status || 'NOT_BOOKED'
          }))}
          total={prebookedTotal}
          bgColor="bg-green-50"
        />
        
        {/* Driver Paid Section */}
        <ExpenseSection 
          title="💰 Driver Paid" 
          items={details.driver_paid_cost_items.map(item => ({
            name: item.name,
            details: `Day ${item.day || 0} • ${item.type || 'activity'}`,
            cost: item.cost || 0,
            vendor: item.vendor || 'Driver',
            status: 'DRIVER_PAID'
          }))}
          total={driverPaidTotal}
          bgColor="bg-yellow-50"
        />
        
        {/* Transportation Section */}
        <TransportationSection 
          title="🚗 Transportation" 
          items={details.transportation_cost_items || []}
          total={transportationTotal}
          bgColor="bg-purple-50"
          driverName={details.transportation_handled_by || 'Driver'}
        />
        
        {/* Grand Total */}
        <div className="bg-gray-100 px-4 py-4 border-t-2 border-gray-300">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-800">💰 GRAND TOTAL</span>
            <span className="text-xl font-bold text-gray-900">IDR {actualTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Cost Comparison */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">💰 Cost Analysis</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">Estimated Cost:</span>
              <span className="font-semibold text-gray-900">IDR {(details.total_estimated_cost || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">Actual Cost:</span>
              <span className="font-semibold text-gray-900">IDR {actualTotal.toLocaleString()}</span>
            </div>
            <div className="border-t border-gray-300 pt-2">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Variance:</span>
                <span className={`font-bold ${actualTotal > (details.total_estimated_cost || 0) ? 'text-red-600' : 'text-green-600'}`}>
                  IDR {Math.abs(actualTotal - (details.total_estimated_cost || 0)).toLocaleString()}
                  {actualTotal > (details.total_estimated_cost || 0) ? ' over' : ' under'}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <h5 className="font-medium text-gray-700 mb-3">Booking Progress</h5>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Items:</span>
                <span className="font-medium">{totalItems}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-green-600">Booked:</span>
                <span className="font-medium text-green-700">{bookedItems}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-yellow-600">Pending Payment:</span>
                <span className="font-medium text-yellow-700">{pendingPayment}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-red-600">Not Booked:</span>
                <span className="font-medium text-red-700">{notBooked}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">📋 Detailed Breakdown</h4>
        
        {/* Day-wise Activities */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
            <h5 className="font-semibold text-green-800">🎫 Activities by Day ({getFilteredItems().length} of {details.prebooked_cost_items.length + details.driver_paid_cost_items.length})</h5>
          </div>
          <div className="p-4">
            {(() => {
              // Group filtered activities by day
              const filteredItems = getFilteredItems();
              
              const itemsByDay = filteredItems.reduce((acc, item) => {
                const day = (item as any).day || (item as any).day_number || 0;
                if (!acc[day]) acc[day] = { prebooked: [], driver_paid: [] };
                if (item.section === 'Prebooked') {
                  acc[day].prebooked.push(item);
                } else if (item.section === 'Driver Paid') {
                  acc[day].driver_paid.push(item);
                }
                return acc;
              }, {} as { [key: number]: { prebooked: any[], driver_paid: any[] } });
              
              const sortedDays = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);
              
              if (sortedDays.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">🔍</div>
                    <p className="font-medium">No items match the selected filters</p>
                    <p className="text-sm mt-1">Try adjusting your filter criteria</p>
                  </div>
                );
              }
              
              return sortedDays.map(day => {
                // Find transportation for this day
                const dayTransportation = details.transportation_cost_items?.find(t => t.day === day);
                const dayDate = itemsByDay[day].prebooked[0]?.date || itemsByDay[day].driver_paid[0]?.date || dayTransportation?.date;
                
                return (
                  <div key={day} className="mb-6 border border-gray-200 rounded-lg">
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                      <h6 className="font-semibold text-gray-800">
                        {day === 0 ? '📋 General Items' : `📅 Day ${day}`}
                        {dayDate && <span className="ml-2 text-sm font-normal text-gray-600">- {formatDate(dayDate)}</span>}
                        <span className="ml-2 text-sm font-normal text-gray-600">
                          ({itemsByDay[day].prebooked.length + itemsByDay[day].driver_paid.length} items)
                        </span>
                      </h6>
                    </div>
                    
                    {/* Prebooked Activities */}
                    {itemsByDay[day].prebooked.length > 0 && (
                      <div className="p-3 border-b border-gray-100">
                        <div className="text-sm font-medium text-blue-700 mb-2">🎫 Prebooked Activities ({itemsByDay[day].prebooked.length})</div>
                        <div className="space-y-2">
                          {itemsByDay[day].prebooked.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-blue-50 rounded border">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{item.name}</div>
                                <div className="text-sm text-gray-600">
                                  {item.vendor && <span>Vendor: {item.vendor} • </span>}
                                  Cost: IDR {(item.cost || 0).toLocaleString()}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.booking_status === 'BOOKED' ? 'bg-blue-100 text-blue-700' :
                                item.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' :
                                item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
                                item.booking_status === 'CANCELED_NO_FEE' ? 'bg-red-50 text-red-600' :
                                item.booking_status === 'CANCELED_WITH_FEE' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.booking_status || 'NOT_BOOKED'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Driver Paid Activities */}
                    {itemsByDay[day].driver_paid.length > 0 && (
                      <div className="p-3 border-b border-gray-100">
                        <div className="text-sm font-medium text-orange-700 mb-2">💰 Driver Paid Activities ({itemsByDay[day].driver_paid.length})</div>
                        <div className="space-y-2">
                          {itemsByDay[day].driver_paid.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-orange-50 rounded border">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{item.name}</div>
                                <div className="text-sm text-gray-600">
                                  {item.vendor && <span>Vendor: {item.vendor} • </span>}
                                  Cost: IDR {(item.cost || 0).toLocaleString()}
                                </div>
                              </div>
                              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
                                Driver Paid
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Transportation */}
                    {dayTransportation && (
                      <div className="p-3">
                        <div className="text-sm font-medium text-purple-700 mb-2">🚗 Transportation</div>
                        <div className="bg-purple-50 rounded border p-3">
                          <div className="font-medium text-gray-900 mb-2">{dayTransportation.trip}</div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Base Cost (IDR)</span>
                              <div className="font-medium">{(dayTransportation.trip_cost || 0).toLocaleString()}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Parking (IDR)</span>
                              <div className="font-medium">{((dayTransportation as any).parking || 0).toLocaleString()}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Overtime (IDR)</span>
                              <div className="font-medium">{((dayTransportation as any).overtime || 0).toLocaleString()}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Total (IDR)</span>
                              <div className="font-bold text-purple-700">
                                {((dayTransportation.trip_cost || 0) + ((dayTransportation as any).parking || 0) + ((dayTransportation as any).overtime || 0)).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Accommodations Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-4 py-2 border-b border-gray-200">
            <h5 className="font-semibold text-blue-800">🏨 Accommodations ({details.accommodation_cost_items.length})</h5>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Hotel</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Room Details</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Dates</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Vendor</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Cost</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {details.accommodation_cost_items.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium">{item.hotel_name}</td>
                    <td className="px-3 py-2 text-gray-600">
                      <div>{item.room_type}</div>
                      <div className="text-xs text-gray-500">
                        {item.no_of_nights} nights • {item.no_of_rooms} rooms
                        {item.no_of_extra_beds > 0 && ` • ${item.no_of_extra_beds} extra beds`}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {formatDate(item.checkin_date)} - {formatDate(item.checkout_date)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{item.vendor || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium">IDR {(item.cost || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.booking_status === 'BOOKED' ? 'bg-blue-100 text-blue-700' :
                        item.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' :
                        item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
                        item.booking_status === 'CANCELED_NO_FEE' ? 'bg-red-50 text-red-600' :
                        item.booking_status === 'CANCELED_WITH_FEE' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.booking_status || 'NOT_BOOKED'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transportation Table */}
        {details.transportation_cost_items && details.transportation_cost_items.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-purple-50 px-4 py-2 border-b border-gray-200">
              <h5 className="font-semibold text-purple-800">🚗 Transportation ({details.transportation_cost_items.length})</h5>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-center font-medium text-gray-700">Day</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Trip</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Trip Cost</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Parking</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Overtime</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {details.transportation_cost_items.map((item, index) => {
                    const tripCost = item.trip_cost || 0;
                    const parking = (item as any).parking || 0;
                    const overtime = (item as any).overtime || 0;
                    const total = tripCost + parking + overtime;
                    
                    return (
                      <tr key={index} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-center text-gray-600">{item.day}</td>
                        <td className="px-3 py-2 font-medium">{item.trip}</td>
                        <td className="px-3 py-2 text-right">IDR {tripCost.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">IDR {parking.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">IDR {overtime.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">IDR {total.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;